<template>
  <div v-if="!isPeopleGroupsView" class="py-4 container-fluid">
    <!-- Dashboard Heading -->
    <div class="dashboard-heading mb-4">
      <div class="dh-icon-wrap dh-icon-admin">
        <i class="material-icons">admin_panel_settings</i>
      </div>
      <div>
        <h5 class="dh-title">Admin Dashboard</h5>
        <p class="dh-subtitle">Monitor activity, manage users, and oversee scheduling operations</p>
      </div>
    </div>
    <div class="row mb-4">
      <div class="col-lg-12">
        <!-- Statistics Cards Row -->
        <div class="row">
          <template v-if="isLoaded">
            <div class="col-lg-3 col-md-6 col-sm-6 my-3" v-for="(card, index) in filteredAdminMenuCards"
              :key="card.id || index">
            <mini-statistics-card :title="getCardTitle(card)"
                @openModal="(card) => openModal(card)"
              :detail="getCardDetail(card)" :icon="getCardIcon(card)" :card="card"
              :card-class="card.id === 'billing' ? billingCardClass : ''" />
            </div>
          </template>
          <!-- Skeleton for Statistics Cards (shown when loading) -->
          <template v-else>
            <div class="col-lg-3 col-md-6 col-sm-6 my-3" v-for="n in 4" :key="`skeleton-${n}`">
              <div class="card h-full mb-2">
                <div class="card-header p-3 pt-2">
                  <div
                    class="icon icon-lg icon-shape shadow text-center border-radius-xl mt-n4 position-absolute bg-gradient-secondary">
                    <div class="skeleton-icon skeleton-animate"></div>
                  </div>
                  <div class="pt-1 text-end">
                    <div class="skeleton-label skeleton-animate mb-2"></div>
                    <div class="skeleton-value skeleton-animate"></div>
                  </div>
                </div>
                <hr class="dark horizontal my-0" />
                <div class="card-footer p-3">
                  <div class="skeleton-detail skeleton-animate mb-1"></div>
                  <div class="skeleton-detail skeleton-animate" style="width: 70%"></div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Dashboard Table -->
        <div class="col-lg-12 my-4">
          <DashboardTable v-if="isLoaded && hasVisibleAdminCards" tableTitle="Admin Menu"
            :tableContent="filteredAdminMenuTable" cardTitle="Upcoming Activities" type="admin"
            :cardContent="sortedUpcomingActivities"
            :highlightSetupWizard="noActivitiesForLocation"
            :statustableContent="generateStatusTableRows()" @view-page="openProjectModal" />
          <CardLoader v-else-if="isLoaded && !hasVisibleAdminCards" />
          <CardLoader v-else />
        </div>

        <div id="admin-people-groups" v-if="isLoaded && stSessionData?.locationid" class="row mb-4">
          <div class="col-lg-12">
            <PeopleGroupsPanel :show-admin-report="true" />
          </div>
        </div>

        <!-- Refresh Button Card -->
        <div class="col-lg-12 my-2" v-if="isLoaded && hasVisibleAdminCards">
          <div class="card refresh-card">
            <div class="card-body py-3 px-4">
              <!-- Centered Refresh Button -->
              <div class="d-flex justify-content-center mb-3">
                <button 
                  class="btn mb-0 refresh-btn"
                  :class="isRefreshing ? 'btn-secondary' : 'btn-primary'"
                  @click="handleRefreshDashboard"
                  :disabled="isRefreshing"
                >
                  <i class="material-icons me-2" :class="{ 'spin-animation': isRefreshing }" style="font-size: 20px; vertical-align: middle;">{{ isRefreshing ? 'sync' : 'refresh' }}</i>
                  {{ isRefreshing ? 'Refreshing...' : 'Refresh Dashboard' }}
                </button>
              </div>
              
              <!-- Auto-refresh Controls -->
              <div class="d-flex align-items-center justify-content-center flex-wrap gap-3">
                <div class="d-flex align-items-center">
                  <label class="form-check-label me-2 text-sm text-secondary" for="autoRefreshToggle">
                    <i class="material-icons text-info me-1" style="font-size: 16px; vertical-align: middle;">timer</i>
                    Auto-refresh
                  </label>
                  <div class="form-check form-switch mb-0">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="autoRefreshToggle"
                      v-model="autoRefreshEnabled"
                      @change="toggleAutoRefresh"
                    />
                  </div>
                </div>
                
                <div v-if="autoRefreshEnabled" class="d-flex align-items-center">
                  <span class="text-sm text-secondary me-2">Interval:</span>
                  <select 
                    class="form-select form-select-sm auto-refresh-select"
                    v-model="autoRefreshInterval"
                    @change="updateAutoRefreshInterval"
                  >
                    <option :value="60000">1 minute</option>
                    <option :value="300000">5 minutes</option>
                    <option :value="600000">10 minutes</option>
                  </select>
                </div>
                
                <div v-if="autoRefreshEnabled" class="d-flex align-items-center">
                  <span class="text-xs text-muted">
                    <i class="material-icons" style="font-size: 14px; vertical-align: middle;">schedule</i>
                    Next refresh in {{ formatCountdown(autoRefreshCountdown) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Status and Activities -->
        <div class="row mt-4" v-if="isLoaded">
          <div class="col-lg-8 mb-4">
            <StatusCard title="Status Overview" :headers="['Item', 'Status', 'Description']"
              :projects="generateStatusTableRows()" :banner="billingStatusBanner" />
          </div>
          <div class="col-lg-4 mb-4" v-if="canViewActivities">
            <div class="card admin-insight-card h-100">
              <div class="card-body d-flex flex-column">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-primary shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10" style="font-size: 24px;">event</i>
                  </div>
                  <h6 class="mb-0 text-sm">Activities</h6>
                </div>
                <div v-if="activitiesCardLoading" class="text-center mb-3">
                  <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                </div>
                <div v-else class="d-flex align-items-end gap-3 mb-3">
                  <span class="count-badge bg-gradient-primary">{{ activitiesCard.count }}</span>
                  <span class="text-xs text-secondary mb-1">active</span>
                </div>
                <div class="text-xs text-secondary flex-grow-1">
                  <div class="d-flex justify-content-between"><span>Auto-generate enabled</span><strong>{{ activitiesCard.auto_generate_count }}</strong></div>
                  <div class="d-flex justify-content-between"><span>Auto-scheduling enabled</span><strong>{{ activitiesCard.auto_schedule_count }}</strong></div>
                  <div class="d-flex justify-content-between"><span>Has future instances/dates</span><strong>{{ activitiesCard.future_instances_count }}</strong></div>
                  <div class="d-flex justify-content-between"><span>Total Activities</span><strong>{{ activitiesCard.total_count }}</strong></div>
                </div>
                <button class="btn btn-sm btn-outline-primary mt-3 mb-0" @click="openActivitiesPage">VIEW DETAILS</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="row my-4">
          <!-- Chart 1 -->
          <div class="col-lg-4 col-md-6 mt-5 mt-md-4">
            <chart-holder-card v-if="isLoaded" title="Email & SMS count" subtitle="Count of daily messages sent"
              update="Count of daily messages sent has been updated">
              <reports-bar-chart id="task-chart" :chart="emailChartData" />
            </chart-holder-card>
            <ChartSkeleton v-else color="primary" />
          </div>

          <!-- Chart 2 -->
          <div class="col-lg-4 col-md-6 mt-5 mt-md-4">
            <chart-holder-card v-if="isLoaded" title="Persons Assigned & Confirmed"
              subtitle="Count of total persons assigned and confirmed per month"
              update="Count of total assigned persons and how many confirmed in each month has been updated"
              color="success">
              <reports-bar-chart id="task-chart-2" :chart="confirmedAssignmentChartData" />
            </chart-holder-card>
            <ChartSkeleton v-else color="success" />
          </div>

          <!-- Chart 3 -->
          <div class="col-lg-4 mt-5 mt-md-4">
            <chart-holder-card v-if="isLoaded" title="Declined assignments count"
              subtitle="Count of assignments that were declined in each month"
              update="Count of assignments that were declined in each month has been updated " color="dark">
              <reports-line-chart id="tasks-chart-3" :chart="declinedAssignmentChartData" />
            </chart-holder-card>
            <ChartSkeleton v-else color="dark" />
          </div>
        </div>

        <!-- Admin Insight Cards Row -->
        <div class="row my-4" v-if="isLoaded && hasAdminPermissions">
          <!-- Card 1: Sign-up Only Workgroups -->
          <div class="col-lg-3 col-md-6 mt-4" v-if="!adminCardsError.signupOnly">
            <div class="card admin-insight-card h-100">
              <div class="card-body d-flex flex-column">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-info shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10" style="font-size: 24px;">how_to_reg</i>
                  </div>
                  <div>
                    <h6 class="mb-0 text-sm">Sign-up Only Workgroups</h6>
                  </div>
                </div>
                <div class="text-center mb-3">
                  <div v-if="adminCardsLoading.signupOnly" class="spinner-border spinner-border-sm text-info" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <span v-else class="count-badge bg-gradient-info">{{ signupOnlyWorkgroups.total_count }}</span>
                </div>
                <p class="text-xs text-secondary mb-3 flex-grow-1">
                  Workgroups configured for self-service signup without auto-scheduling
                </p>
                <div class="text-center mt-auto">
                  <button 
                    class="btn btn-sm btn-outline-info mb-0"
                    :disabled="signupOnlyWorkgroups.total_count === 0"
                    @click="showSignupOnlyModal = true"
                  >
                    {{ signupOnlyWorkgroups.total_count === 0 ? 'No Items' : 'VIEW DETAILS' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 2: Pending Skill Interest Requests -->
          <div class="col-lg-3 col-md-6 mt-4" v-if="!adminCardsError.skillInterests">
            <div class="card admin-insight-card h-100">
              <div class="card-body d-flex flex-column">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-warning shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10" style="font-size: 24px;">pending_actions</i>
                  </div>
                  <div>
                    <h6 class="mb-0 text-sm">Pending Skill Interest Requests</h6>
                  </div>
                </div>
                <div class="text-center mb-3">
                  <div v-if="adminCardsLoading.skillInterests" class="spinner-border spinner-border-sm text-warning" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <span v-else class="count-badge bg-gradient-warning">{{ pendingSkillInterests.total_count }}</span>
                </div>
                <p class="text-xs text-secondary mb-3 flex-grow-1">
                  Volunteer requests to be added to skills awaiting admin approval
                </p>
                <div class="text-center mt-auto">
                  <button 
                    class="btn btn-sm btn-outline-warning mb-0"
                    :disabled="pendingSkillInterests.total_count === 0"
                    @click="showSkillInterestsModal = true"
                  >
                    {{ pendingSkillInterests.total_count === 0 ? 'No Items' : 'VIEW DETAILS' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 3: Workgroups Needing Volunteers -->
          <div class="col-lg-3 col-md-6 mt-4" v-if="!adminCardsError.needingVolunteers">
            <div class="card admin-insight-card h-100">
              <div class="card-body d-flex flex-column">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-danger shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10" style="font-size: 24px;">group_add</i>
                  </div>
                  <div>
                    <h6 class="mb-0 text-sm">Workgroups Needing Volunteers</h6>
                  </div>
                </div>
                <div class="text-center mb-3">
                  <div v-if="adminCardsLoading.needingVolunteers" class="spinner-border spinner-border-sm text-danger" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <span v-else class="count-badge bg-gradient-danger">{{ workgroupsNeedingVolunteers.total_count }}</span>
                </div>
                <p class="text-xs text-secondary mb-3 flex-grow-1">
                  Upcoming activity workgroups that still have unfilled positions
                </p>
                <div class="text-center mt-auto">
                  <button 
                    class="btn btn-sm btn-outline-danger mb-0"
                    :disabled="workgroupsNeedingVolunteers.total_count === 0"
                    @click="showNeedingVolunteersModal = true"
                  >
                    {{ workgroupsNeedingVolunteers.total_count === 0 ? 'No Items' : 'VIEW DETAILS' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 4: Volunteer Assignment Insights -->
          <div class="col-lg-3 col-md-6 mt-4" v-if="canViewActivities">
            <div class="card admin-insight-card h-100">
              <div class="card-body d-flex flex-column">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-success shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10" style="font-size: 24px;">insights</i>
                  </div>
                  <div>
                    <h6 class="mb-0 text-sm">Volunteer Assignment Insights</h6>
                  </div>
                </div>
                <div class="text-center mb-3">
                  <span class="count-badge bg-gradient-success">
                    <i class="material-icons" style="font-size: 16px; vertical-align: middle;">bar_chart</i>
                  </span>
                </div>
                <p class="text-xs text-secondary mb-3 flex-grow-1">
                  View top volunteers, decline rates, no-shows, and confirm rates across the location
                </p>
                <div class="text-center mt-auto">
                  <button
                    class="btn btn-sm btn-outline-success mb-0"
                    @click="showVolunteerStatsModal = true"
                  >
                    VIEW INSIGHTS
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Admin Reports Card -->
        <div class="row mb-4" v-if="isLoaded && hasAdminPermissions">
          <div class="col-12">
            <AdminReportsCard :locationId="getLocationId()" />
          </div>
        </div>

        <br>

        <!-- Additional Statistics Cards Row -->
        <div class="row row-3">
          <!-- Auto-Engine Executions Card -->
          <div class="col-lg-3 col-md-6 col-sm-6 mt-4" v-if="isLoaded && hasAuthorizedAdminCard('schedulingUnits')">
            <div class="card h-100">
              <div class="card-body p-3">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-primary shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10">schedule</i>
                  </div>
                  <h6 class="mb-0 text-sm">Auto-Engine Executions</h6>
                </div>
                <div class="d-flex flex-column gap-2 mb-3">
                  <div class="d-flex justify-content-between align-items-center px-2 py-1 rounded" style="background:#f8f9fa;">
                    <div class="d-flex align-items-center gap-2">
                      <i class="material-icons text-info" style="font-size:18px;">event</i>
                      <span class="text-sm fw-semibold">Auto-Schedule</span>
                    </div>
                    <div class="text-end">
                      <span class="text-sm fw-bold">{{ autoEngineCardData.autoschedule_count }}</span>
                    </div>
                  </div>
                  <div class="d-flex justify-content-between align-items-center px-2 py-1 rounded" style="background:#f8f9fa;">
                    <div class="d-flex align-items-center gap-2">
                      <i class="material-icons text-warning" style="font-size:18px;">swap_horiz</i>
                      <span class="text-sm fw-semibold">Auto-Substitute</span>
                    </div>
                    <div class="text-end">
                      <span class="text-sm fw-bold">{{ autoEngineCardData.autosubstitute_count }}</span>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-between align-items-center border-top pt-2">
                  <span class="text-xs text-secondary fw-bold">Total Cost</span>
                  <span class="text-sm fw-bold">${{ parseFloat(autoEngineCardData.total_cost || 0).toFixed(2) }}</span>
                </div>
              </div>
              <div class="card-footer p-2 text-center border-top">
                <button class="btn btn-sm btn-outline-primary mb-0" @click="openModal({ id: 'schedulingUnits' })">VIEW DETAILS</button>
              </div>
            </div>
          </div>
          <!-- Combined Messaging Units Card -->
          <div class="col-lg-3 col-md-6 col-sm-6 mt-4" v-if="isLoaded && (hasAuthorizedAdminCard('messagingUnitSMS') || hasAuthorizedAdminCard('messagingUnitEmail'))">
            <div class="card h-100">
              <div class="card-body p-3">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-success shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10">forum</i>
                  </div>
                  <h6 class="mb-0 text-sm">Messaging Units</h6>
                </div>
                <div class="d-flex flex-column gap-2 mb-3">
                  <div class="px-2 py-1 rounded" style="background:#f8f9fa;">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center gap-2">
                        <i class="material-icons text-success" style="font-size:18px;">sms</i>
                        <span class="text-sm fw-semibold">SMS</span>
                      </div>
                      <div class="text-end">
                        <span class="text-sm fw-bold">{{ mtdSms.count }} msgs</span>
                        <span class="text-xs text-muted ms-2">${{ mtdSms.cost.toFixed(2) }}</span>
                      </div>
                    </div>
                    <div v-if="smsThreshold" class="mt-1">
                      <div class="progress" style="height:3px;">
                        <div class="progress-bar" :class="'bg-' + smsThreshold.color" :style="{ width: smsThreshold.pct + '%' }" role="progressbar"></div>
                      </div>
                      <span class="text-xs" :class="'text-' + smsThreshold.color">{{ smsThreshold.pct }}% of ${{ smsThreshold.limit.toFixed(2) }} limit</span>
                    </div>
                  </div>
                  <div class="d-flex justify-content-between align-items-center px-2 py-1 rounded" style="background:#f8f9fa;">
                    <div class="d-flex align-items-center gap-2">
                      <i class="material-icons text-info" style="font-size:18px;">email</i>
                      <span class="text-sm fw-semibold">Email</span>
                    </div>
                    <div class="text-end">
                      <span class="text-sm fw-bold">{{ mtdEmail.count }} msgs</span>
                      <span class="text-xs text-muted ms-2">${{ mtdEmail.cost.toFixed(2) }}</span>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-between align-items-center border-top pt-2">
                  <span class="text-xs text-secondary fw-bold">Total Cost</span>
                  <span class="text-sm fw-bold">${{ (mtdSms.cost + mtdEmail.cost).toFixed(2) }}</span>
                </div>
              </div>
              <div class="card-footer p-2 text-center border-top">
                <button class="btn btn-sm btn-outline-success mb-0" @click="showMessagingStatsModal = true">VIEW DETAILS</button>
              </div>
            </div>
          </div>
          <!-- AI Units Card -->
          <div class="col-lg-3 col-md-6 col-sm-6 mt-4" v-if="isLoaded && (hasAuthorizedAdminCard('messagingUnitSMS') || hasAuthorizedAdminCard('messagingUnitEmail'))">
            <div class="card h-100">
              <div class="card-body p-3">
                <div class="d-flex align-items-center mb-3">
                  <div class="icon icon-shape bg-gradient-info shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10">smart_toy</i>
                  </div>
                  <h6 class="mb-0 text-sm">AI Units</h6>
                </div>
                <div class="d-flex flex-column gap-2 mb-3">
                  <div class="px-2 py-1 rounded" style="background:#f8f9fa;">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-sm fw-semibold">AI Chat</span>
                      <span class="text-sm fw-bold">${{ mtdAiChat.cost.toFixed(2) }}</span>
                    </div>
                    <div class="d-flex gap-3 mt-1">
                      <span class="text-xs text-muted">Input: {{ mtdAiChat.input_tokens.toLocaleString() }}</span>
                      <span class="text-xs text-muted">Output: {{ mtdAiChat.output_tokens.toLocaleString() }}</span>
                    </div>
                    <div v-if="aiChatThreshold" class="mt-1">
                      <div class="progress" style="height:3px;">
                        <div class="progress-bar" :class="'bg-' + aiChatThreshold.color" :style="{ width: aiChatThreshold.pct + '%' }" role="progressbar"></div>
                      </div>
                      <span class="text-xs" :class="'text-' + aiChatThreshold.color">{{ aiChatThreshold.pct }}% of ${{ aiChatThreshold.limit.toFixed(2) }} limit</span>
                    </div>
                  </div>
                  <div class="px-2 py-1 rounded" style="background:#f8f9fa;">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-sm fw-semibold">AI Schedule Gen</span>
                      <span class="text-sm fw-bold">${{ mtdAiScheduler.cost.toFixed(2) }}</span>
                    </div>
                    <div class="d-flex gap-3 mt-1">
                      <span class="text-xs text-muted">Input: {{ mtdAiScheduler.input_tokens.toLocaleString() }}</span>
                      <span class="text-xs text-muted">Output: {{ mtdAiScheduler.output_tokens.toLocaleString() }}</span>
                    </div>
                    <div v-if="aiSchedulerThreshold" class="mt-1">
                      <div class="progress" style="height:3px;">
                        <div class="progress-bar" :class="'bg-' + aiSchedulerThreshold.color" :style="{ width: aiSchedulerThreshold.pct + '%' }" role="progressbar"></div>
                      </div>
                      <span class="text-xs" :class="'text-' + aiSchedulerThreshold.color">{{ aiSchedulerThreshold.pct }}% of ${{ aiSchedulerThreshold.limit.toFixed(2) }} limit</span>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-between align-items-center border-top pt-2">
                  <span class="text-xs text-secondary fw-bold">Total AI Cost</span>
                  <span class="text-sm fw-bold">${{ (mtdAiChat.cost + mtdAiScheduler.cost).toFixed(2) }}</span>
                </div>
              </div>
              <div class="card-footer p-2 text-center border-top">
                <button class="btn btn-sm btn-outline-info mb-0" @click="showAiUnitsModal = true">VIEW DETAILS</button>
              </div>
            </div>
          </div>
          <!-- Month-to-date Usage Card -->
          <div class="col-lg-3 col-md-6 col-sm-6 mt-4" v-if="isLoaded && hasAuthorizedAdminCard('usage')">
            <div class="card h-100">
              <div class="card-body p-3">
                <div class="d-flex align-items-center mb-2">
                  <div class="icon icon-shape bg-gradient-warning shadow text-center border-radius-lg me-3">
                    <i class="material-icons opacity-10">paid</i>
                  </div>
                  <div>
                    <p class="text-sm mb-0 text-capitalize font-weight-bold">Month-to-date Usage</p>
                    <h5 class="font-weight-bolder mb-0">${{ monthToDateTotalUsage.toFixed(2) }}</h5>
                  </div>
                </div>
                <div v-if="overallThreshold" class="mb-2">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-xs text-secondary">Spend Limit</span>
                    <span class="text-xs fw-bold" :class="'text-' + overallThreshold.color">${{ monthToDateTotalUsage.toFixed(2) }} / ${{ overallThreshold.limit.toFixed(2) }}</span>
                  </div>
                  <div class="progress" style="height:5px;">
                    <div class="progress-bar" :class="'bg-' + overallThreshold.color" :style="{ width: overallThreshold.pct + '%' }" role="progressbar"></div>
                  </div>
                  <div v-if="overallThreshold.pct >= 90" class="d-flex align-items-center gap-1 mt-1">
                    <i class="material-icons text-danger" style="font-size:14px;">warning</i>
                    <span class="text-xs text-danger fw-bold">Approaching spend limit!</span>
                  </div>
                  <div v-else-if="overallThreshold.pct >= 70" class="d-flex align-items-center gap-1 mt-1">
                    <i class="material-icons text-warning" style="font-size:14px;">info</i>
                    <span class="text-xs text-warning fw-bold">{{ overallThreshold.pct }}% of budget used</span>
                  </div>
                </div>
                <div v-else class="mb-2">
                  <span class="text-xs text-muted">No overall spend limit set</span>
                </div>
                <div class="text-xs" v-html="usageDetail"></div>
              </div>
              <div class="card-footer p-2 text-center border-top">
                <button class="btn btn-sm btn-outline-warning mb-0" @click="openModal({ id: 'usage', label: 'Month-to-date Usage', uri: '' })">VIEW DETAILS</button>
              </div>
            </div>
          </div>
          <!-- <div
            class="col-lg-3 col-md-6 col-sm-6 mt-4"
            v-if="isLoaded && hasAuthorizedAdminCard('admin_pending_swaps')"
          >
            <mini-statistics-card
              :title="{
                text: getAuthorizedAdminCard('admin_pending_swaps')?.label || 'Pending Swaps',
                value: `${stLocationPendingSwapRequests?.total_pending_requests || 0}`
              }"
              :detail="pendingSwapsDetail"
              :icon="{
                name: 'leaderboard',
                color: 'text-white',
                background: 'primary',
              }"
            />
          </div>
          <div
            class="col-lg-3 col-md-6 col-sm-6 mt-4"
            v-if="isLoaded && hasAuthorizedAdminCard('usage')"
          >
            <mini-statistics-card
              :title="{
                text: getAuthorizedAdminCard('usage')?.label || 'Usage',
                value: `$${(stMonthToDateUsage?.auto_engine?.total_cost || 0).toFixed(4)}`
              }"
              :detail="usageDetail"
              :icon="{
                name: 'weekend',
                color: 'text-white',
                background: 'info',
              }"
              :card="{ id: 'usage', label: 'Month-to-date Usage', uri: '' }"
              @openModal="openModal"
            />
          </div>
          <div
            class="col-lg-3 col-md-6 col-sm-6 mt-4"
            v-if="isLoaded && hasAuthorizedAdminCard('messageTracker')"
          >
            <mini-statistics-card
              :title="{
                text: getAuthorizedAdminCard('messageTracker')?.label || 'AI Units',
                value: `$${(stMonthToDateUsage?.auto_engine?.total_cost || 0).toFixed(4)}`
              }"
              :detail="usageDetail"
              :icon="{
                name: 'robot_2',
                color: 'text-white',
                background: 'info',
              }"
            />
          </div> -->
        </div>

        <!-- Messages Table -->
        <div class="mt-4" style="margin-top: 80px !important;" v-if="isLoaded && hasAuthorizedAdminCard('messageTracker')">
          <BaseTable heading="Messages" :rows="messagingSummaryRows" :columns="messagingColumns" idKey="index"
            searchable :selectable="false" :loading="!isLoaded" :pagination="stMessagingSummaryPagination"
            @changePage="onMessagingPageChange" @updatePageSize="onMessagingPageSizeChange"
            @searchChanged="onMessagingSearchChanged">
            <template #buttons>
              <div class="d-flex align-items-center gap-2">
                <span class="text-sm text-secondary me-1"><i class="material-icons text-sm align-middle me-1">filter_list</i>Type:</span>
                <div class="form-check form-check-inline mb-0">
                  <input class="form-check-input" type="checkbox" id="msgTypeEmail" value="email" v-model="selectedMessageTypes" @change="onMessageTypeFilterChange">
                  <label class="form-check-label text-sm" for="msgTypeEmail">Email</label>
                </div>
                <div class="form-check form-check-inline mb-0">
                  <input class="form-check-input" type="checkbox" id="msgTypeSms" value="sms" v-model="selectedMessageTypes" @change="onMessageTypeFilterChange">
                  <label class="form-check-label text-sm" for="msgTypeSms">SMS</label>
                </div>
                <div class="form-check form-check-inline mb-0">
                  <input class="form-check-input" type="checkbox" id="msgTypeAiChat" value="ai_chat" v-model="selectedMessageTypes" @change="onMessageTypeFilterChange">
                  <label class="form-check-label text-sm" for="msgTypeAiChat">AI Chat</label>
                </div>
                <div class="form-check form-check-inline mb-0">
                  <input class="form-check-input" type="checkbox" id="msgTypeSmsAiChat" value="sms_ai_chat" v-model="selectedMessageTypes" @change="onMessageTypeFilterChange">
                  <label class="form-check-label text-sm" for="msgTypeSmsAiChat">SMS AI Chat</label>
                </div>
                <div class="form-check form-check-inline mb-0">
                  <input class="form-check-input" type="checkbox" id="msgTypeAiScheduler" value="ai_scheduler" v-model="selectedMessageTypes" @change="onMessageTypeFilterChange">
                  <label class="form-check-label text-sm" for="msgTypeAiScheduler">AI Scheduler</label>
                </div>
              </div>
            </template>
            <template #col-message_data="{ row }">
              <span class="text-truncate d-inline-block" style="max-width: 200px;"
                :title="htmlToText(row.message_data)">
                {{ truncateText(htmlToText(row.message_data), 30) }}
              </span>
            </template>
            <template #col-message_subject="{ row }">
              <span class="text-truncate d-inline-block" style="max-width: 280px;"
                :title="row.message_subject">
                {{ truncateText(row.message_subject || '', 45) }}
              </span>
            </template>
            <template #col-rate="{ row }">
              <span>${{ row.rate }}</span>
            </template>
            <template #col-delivery_status="{ row }">
              <div class="d-flex align-items-center gap-1">
                <span v-if="getStatusBadge(row)" :class="getStatusBadgeClass(getStatusBadge(row).category)">
                  {{ getStatusBadge(row).emoji }} {{ getStatusBadge(row).label }}
                </span>
                <button v-if="row.message_type === 'email' || row.message_type === 'sms'"
                  class="btn btn-sm btn-outline-secondary my-0 py-0 px-1"
                  style="font-size:11px;line-height:1.5;"
                  @click.stop="checkMessageStatus(row)"
                  :disabled="checkingStatusFor === row.message_id"
                  :title="getStatusBadge(row) ? 'Refresh status' : 'Check delivery status'">
                  <span v-if="checkingStatusFor === row.message_id" class="spinner-border spinner-border-sm" style="width:10px;height:10px;" role="status"></span>
                  <i v-else class="material-icons" style="font-size:14px;vertical-align:middle;">{{ getStatusBadge(row) ? 'refresh' : 'search' }}</i>
                </button>
              </div>
            </template>
            <template #actions="{ row }">
              <button class="btn btn-sm btn-outline-info my-0" @click="openMessageDetails(row)">
                <i class="material-icons text-sm align-middle me-1">info</i>
                View Message
              </button>
            </template>
          </BaseTable>
        </div>

        <!-- Audit Events Card -->
        <div class="mt-4" v-if="isLoaded && hasAuthorizedAdminCard('messageTracker')">
          <AuditEventsCard scope="location" />
        </div>

        <div class="mt-4" v-if="isLoaded && hasAdminPermissions">
          <VolunteerInvitationAdminCard scope="location" :location-id="stSessionData?.locationid || getLocationId()" />
        </div>

      </div>
    </div>
  </div>

  <div v-if="isPeopleGroupsView" class="py-4 container-fluid">
    <PeopleGroupsPanel :show-admin-report="true" />
  </div>

  <!-- Modals -->
  <Modal v-if="isModalOpen" :modalHeading="modalHeading" :modalContent="modalContent" :iframeUrl="iframeUrl"
    refresh-on-close @close="closeModal" />
  <ContentModal v-model="showPendingRegistrationModal" title="Pending Registrations" size="2xl">
    <PendingRegistrations v-if="hasAuthorizedAdminCard('pendingRegistration') && showPendingRegistrationModal" />
  </ContentModal>
  <ContentModal v-model="showUsersModal" title="Users" size="xl">
    <Users v-if="hasAuthorizedAdminCard('numberOfUsers') && showUsersModal" />
  </ContentModal>
  <ContentModal v-model="showMessageDetailsModal" title="Message Details" size="lg">
    <template #default>
      <div v-if="selectedMessage" class="message-details">
        <dl class="log-details-list">
          <div class="log-item">
            <dt>Date</dt>
            <dd>{{ selectedMessage.message_dt }}</dd>
          </div>

          <div class="log-item">
            <dt>Subject</dt>
            <dd>{{ selectedMessage.message_subject }}</dd>
          </div>

          <div class="log-item">
            <dt>Message</dt>
            <dd class="log-message" style="white-space: pre-wrap; word-wrap: break-word;">
              {{ htmlToText(selectedMessage.message_data) }}
            </dd>
          </div>

          <div class="log-item">
            <dt>Cost</dt>
            <dd>${{ selectedMessage.rate }}</dd>
          </div>

          <div class="log-item">
            <dt>Recipient</dt>
            <dd>{{ selectedMessage.recipient_address || '-' }}</dd>
          </div>

          <div class="log-item" v-if="selectedMessage.message_type === 'email' || selectedMessage.message_type === 'sms'">
            <dt>Delivery Status</dt>
            <dd>
              <div v-if="getStatusBadge(selectedMessage)" class="mb-2">
                <span :class="getStatusBadgeClass(getStatusBadge(selectedMessage).category)" style="font-size:13px;">
                  {{ getStatusBadge(selectedMessage).emoji }} {{ getStatusBadge(selectedMessage).label }}
                </span>
              </div>
              <div v-if="selectedMessage.status_output" class="mt-2" style="font-size:13px;color:#475569;">
                <div v-for="(line, idx) in getStatusSummaryLines(selectedMessage)" :key="idx">
                  {{ line }}
                </div>
              </div>
              <button class="btn btn-sm btn-outline-primary mt-2"
                @click="checkMessageStatus(selectedMessage)"
                :disabled="checkingStatusFor === selectedMessage.message_id">
                <span v-if="checkingStatusFor === selectedMessage.message_id" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="material-icons text-sm align-middle me-1">{{ getStatusBadge(selectedMessage) ? 'refresh' : 'search' }}</i>
                {{ getStatusBadge(selectedMessage) ? 'Refresh Status' : 'Check Status' }}
              </button>
            </dd>
          </div>
        </dl>
      </div>
    </template>
  </ContentModal>
  <ContentModal v-model="showPendingSwapsModal" title="Location Pending Swap Requests" size="2xl">
    <template #default>
      <BaseTable
        heading=""
        :rows="pendingSwapsRows"
        :columns="pendingSwapsColumns"
        idKey="swap_id"
        searchable
        :selectable="false"
        :loading="pendingSwapsLoading"
      >
        <template #col-swap_description="{ row }">
          <button
            type="button"
            class="btn btn-link p-0 text-start text-truncate d-inline-block"
            style="max-width: 400px;"
            :title="`View full description: ${row.swap_description}`"
            @click="openPendingSwapDescription(row)"
          >
            {{ truncateText(row.swap_description, 80) }}
          </button>
        </template>
        <template #col-swap_status="{ row }">
          <span :class="{
            'badge bg-gradient-warning': row.swap_status === 'pending',
            'badge bg-gradient-success': row.swap_status === 'approved',
            'badge bg-gradient-danger': row.swap_status === 'rejected'
          }">
            {{ row.swap_status }}
          </span>
        </template>
      </BaseTable>
    </template>
  </ContentModal>
  <ContentModal v-model="showPendingSwapDescriptionModal" title="Swap Request Description" size="lg">
    <p class="mb-0 text-break" style="white-space: pre-wrap;">{{ selectedPendingSwap?.swap_description || '-' }}</p>
  </ContentModal>
  
  <!-- Admin Insight Card Modals -->
  <ContentModal v-model="showSignupOnlyModal" title="Sign-up Only Workgroups" size="2xl">
    <template #default>
      <BaseTable
        heading=""
        :rows="signupOnlyRows"
        :columns="signupOnlyColumns"
        idKey="id"
        searchable
        :selectable="false"
        :loading="adminCardsLoading.signupOnly"
      >
        <template #col-instance_datetime="{ row }">
          <span>{{ row.instance_datetime || '-' }}</span>
        </template>
        <template #col-signup_origin="{ row }">
          <span :class="{
            'badge bg-gradient-info': row.signup_origin === 'converted',
            'badge bg-gradient-success': row.signup_origin === 'initial_setup',
            'badge bg-gradient-secondary': row.signup_origin === 'manual'
          }">
            {{ row.signup_origin || '-' }}
          </span>
        </template>
        <template #actions="{ row }">
          <button
            class="btn btn-sm btn-outline-info my-0"
            @click="openSignupSummary(row.id)"
            title="View notification summary for this workgroup"
          >
            <i class="material-icons text-sm align-middle me-1">summarize</i>
            Summary
          </button>
        </template>
      </BaseTable>
    </template>
  </ContentModal>
  
  <SignupSummaryModal
    v-model="showSignupSummaryModal"
    :locationId="getLocationId()"
    :aiwgId="selectedSignupAiwgId"
  />
  
  <ContentModal v-model="showSkillInterestsModal" title="Pending Skill Interest Requests" size="2xl">
    <template #default>
      <BaseTable
        heading=""
        :rows="skillInterestsRows"
        :columns="skillInterestsColumns"
        idKey="id"
        searchable
        :selectable="false"
        :loading="adminCardsLoading.skillInterests"
      >
        <template #col-request_date="{ row }">
          <span>{{ row.request_date || '-' }}</span>
        </template>
        <template #col-workgroups="{ row }">
          <span class="text-truncate d-inline-block" style="max-width: 200px;" :title="row.workgroups">
            {{ truncateText(row.workgroups, 50) }}
          </span>
        </template>
        <template #actions="{ row }">
          <button 
            class="btn btn-sm btn-success my-0" 
            @click="handleAssignSkillToUser(row)"
            :disabled="assigningSkill[row.id]"
          >
            <i v-if="!assigningSkill[row.id]" class="material-icons text-sm align-middle me-1">person_add</i>
            <span v-if="assigningSkill[row.id]" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ assigningSkill[row.id] ? 'Assigning...' : 'Assign' }}
          </button>
        </template>
      </BaseTable>
    </template>
  </ContentModal>
  
  <ContentModal v-model="showNeedingVolunteersModal" title="Workgroups Needing Volunteers" size="2xl">
    <template #default>
      <BaseTable
        heading=""
        :rows="needingVolunteersRows"
        :columns="needingVolunteersColumns"
        idKey="id"
        searchable
        :selectable="false"
        :loading="adminCardsLoading.needingVolunteers"
      >
        <template #col-instance_datetime="{ row }">
          <span>{{ row.instance_datetime || '-' }}</span>
        </template>
        <template #col-openings_remaining="{ row }">
          <span :class="{
            'badge bg-gradient-danger': row.openings_remaining > 0,
            'badge bg-gradient-success': row.openings_remaining === 0
          }">
            {{ row.openings_remaining }}
          </span>
        </template>
      </BaseTable>
    </template>
  </ContentModal>

  <!-- Volunteer Assignment Insights Modal -->
  <VolunteerStatsModal
    v-model="showVolunteerStatsModal"
    :locationId="getLocationId()"
  />

  <MessagingStatsModal
    v-model="showMessagingStatsModal"
    :locationId="getLocationId()"
  />

  <!-- AI Units Detail Modal -->
  <ContentModal v-model="showAiUnitsModal" title="AI Units — Month-to-Date" size="lg">
    <template #default>
      <div class="mb-4">
        <h6 class="fw-bold mb-3">Current Month</h6>
        <div class="d-flex flex-column gap-2">
          <div class="d-flex justify-content-between align-items-center px-3 py-2 rounded" style="background:#f8f9fa;">
            <div>
              <div class="fw-semibold">AI Chat</div>
              <div class="text-xs text-muted">Input: {{ mtdAiChat.input_tokens.toLocaleString() }} · Output: {{ mtdAiChat.output_tokens.toLocaleString() }}</div>
            </div>
            <span class="fw-bold">${{ mtdAiChat.cost.toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center px-3 py-2 rounded" style="background:#f8f9fa;">
            <div>
              <div class="fw-semibold">AI Schedule Generator</div>
              <div class="text-xs text-muted">Input: {{ mtdAiScheduler.input_tokens.toLocaleString() }} · Output: {{ mtdAiScheduler.output_tokens.toLocaleString() }}</div>
            </div>
            <span class="fw-bold">${{ mtdAiScheduler.cost.toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center px-3 pt-2 border-top">
            <span class="fw-bold">Total AI Cost</span>
            <span class="fw-bold">${{ (mtdAiChat.cost + mtdAiScheduler.cost).toFixed(2) }}</span>
          </div>
        </div>
      </div>
      <div v-if="prevAiChat || prevAiScheduler">
        <h6 class="fw-bold mb-3">Previous Month</h6>
        <div class="d-flex flex-column gap-2">
          <div class="d-flex justify-content-between align-items-center px-3 py-2 rounded" style="background:#f8f9fa;">
            <div>
              <div class="fw-semibold">AI Chat</div>
              <div class="text-xs text-muted">Input: {{ (prevAiChat?.input_tokens || 0).toLocaleString() }} · Output: {{ (prevAiChat?.output_tokens || 0).toLocaleString() }}</div>
            </div>
            <span class="fw-bold">${{ parseFloat(prevAiChat?.cost || 0).toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center px-3 py-2 rounded" style="background:#f8f9fa;">
            <div>
              <div class="fw-semibold">AI Schedule Generator</div>
              <div class="text-xs text-muted">Input: {{ (prevAiScheduler?.input_tokens || 0).toLocaleString() }} · Output: {{ (prevAiScheduler?.output_tokens || 0).toLocaleString() }}</div>
            </div>
            <span class="fw-bold">${{ parseFloat(prevAiScheduler?.cost || 0).toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center px-3 pt-2 border-top">
            <span class="fw-bold">Total AI Cost</span>
            <span class="fw-bold">${{ (parseFloat(prevAiChat?.cost || 0) + parseFloat(prevAiScheduler?.cost || 0)).toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </template>
  </ContentModal>

  <!-- Admin Permissions Modal -->
  <AdminPermissionsModal
    v-model="showAdminPermissionsModal"
    personName=""
    :locationName="stLocationDetail?.locationName || ''"
    :prefetchedData="ownAdminPermissions"
  />

  <!-- Auto-Engine Tracker Modal -->
  <ContentModal v-model="showAutoEngineTrackerModal" title="Auto-Engine Execution Tracker" size="2xl">
    <template #default>
      <!-- Location / Org / Date info -->
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div class="text-sm text-secondary">
          <div v-if="autoEngineTrackerData?.location || autoEngineTrackerData?.organization">
            <span v-if="autoEngineTrackerData?.organization"><b>Organization:</b> {{ autoEngineTrackerData.organization.name }}</span>
            <span v-if="autoEngineTrackerData?.organization && autoEngineTrackerData?.location"> &bull; </span>
            <span v-if="autoEngineTrackerData?.location"><b>Location:</b> {{ autoEngineTrackerData.location.name }}</span>
            <span v-if="autoEngineTrackerData?.location?.timezone" class="text-muted"> ({{ autoEngineTrackerData.location.timezone }})</span>
          </div>
          <div v-if="autoEngineTrackerData?.date_range">
            <b>Date range:</b> {{ autoEngineTrackerData.date_range?.from_date || '-' }} &rarr; {{ autoEngineTrackerData.date_range?.to_date || '-' }}
          </div>
        </div>
      </div>

      <!-- Filters row -->
      <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
        <input type="date" class="form-control form-control-sm" style="max-width:150px" v-model="autoEngineTrackerFilters.from_date" />
        <span class="text-muted">to</span>
        <input type="date" class="form-control form-control-sm" style="max-width:150px" v-model="autoEngineTrackerFilters.to_date" />
        <select class="form-select form-select-sm" style="max-width:160px" v-model="autoEngineTrackerFilters.execution_type">
          <option value="">All types</option>
          <option value="autoschedule">Auto Schedule</option>
          <option value="autosubstitute">Auto Substitute</option>
          <option value="userinitiated">User Initiated</option>
        </select>
        <input type="text" class="form-control form-control-sm" style="max-width:150px" placeholder="Workgroup..." v-model="autoEngineTrackerSearchWorkgroup" />
        <input type="text" class="form-control form-control-sm" style="max-width:150px" placeholder="Activity..." v-model="autoEngineTrackerSearchActivity" />
        <button class="btn btn-sm btn-primary m-0" style="white-space:nowrap" @click="applyAutoEngineFilters" :disabled="autoEngineTrackerLoading">
          <i v-if="!autoEngineTrackerLoading" class="material-icons text-sm align-middle me-1">filter_list</i>
          <span v-if="!autoEngineTrackerLoading">Apply Filters</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
        <button class="btn btn-sm btn-outline-secondary m-0" style="white-space:nowrap" @click="resetAutoEngineFilters" :disabled="autoEngineTrackerLoading">
          <i class="material-icons text-sm align-middle me-1">refresh</i>Reset
        </button>
      </div>

      <!-- Summary -->
      <div v-if="autoEngineTrackerData?.summary" class="card mb-3">
        <div class="card-body py-3 px-4">
          <div class="d-flex flex-wrap align-items-center gap-4 mb-2">
            <div>
              <div class="text-xs text-secondary">Total Executions</div>
              <div class="h6 m-0">{{ autoEngineTrackerData.summary.total_executions ?? 0 }}</div>
            </div>
            <div>
              <div class="text-xs text-secondary">Total Cost</div>
              <div class="h6 m-0">${{ parseFloat(autoEngineTrackerData.summary.total_cost ?? 0).toFixed(4) }}</div>
            </div>
            <div>
              <div class="text-xs text-secondary">Workgroups Updated by AI</div>
              <div class="h6 m-0">
                <span :class="(autoEngineTrackerData.summary.ai_updated_by_ai_count ?? 0) > 0 ? 'text-success' : ''">
                  {{ autoEngineTrackerData.summary.ai_updated_by_ai_count ?? 0 }}
                </span>
              </div>
            </div>
          </div>
          <!-- By-type breakdown -->
          <div v-if="autoEngineTrackerData.summary.by_type" class="d-flex flex-wrap gap-3 mt-2">
            <div v-for="(typeData, typeKey) in autoEngineTrackerData.summary.by_type" :key="typeKey"
              class="d-flex align-items-center gap-2 px-3 py-2 rounded" style="background:#f8f9fa;">
              <span :class="{
                'badge bg-gradient-info': typeKey === 'autoschedule',
                'badge bg-gradient-warning': typeKey === 'autosubstitute',
                'badge bg-gradient-secondary': typeKey === 'userinitiated'
              }">{{ typeKey }}</span>
              <span class="text-sm">
                <b>{{ typeData.count }}</b> exec &bull; ${{ parseFloat(typeData.cost ?? 0).toFixed(4) }}
              </span>
              <span class="text-xs text-muted" v-if="typeData.description" :title="typeData.description">
                {{ truncateText(typeData.description, 40) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Info banner -->
      <div v-if="autoEngineTrackerData?.info" class="text-xs text-muted mb-2">
        <span v-if="autoEngineTrackerData.info.purpose">{{ autoEngineTrackerData.info.purpose }}</span>
        <span v-if="autoEngineTrackerData.info.rate_explanation"> &mdash; {{ autoEngineTrackerData.info.rate_explanation }}</span>
      </div>

      <!-- Records table -->
      <BaseTable
        heading="Execution Records"
        :rows="autoEngineTrackerRows"
        :columns="autoEngineTrackerColumns"
        idKey="id"
        :searchable="false"
        :selectable="false"
        :loading="autoEngineTrackerLoading"
        :pagination="autoEngineTrackerPagination"
        @changePage="onAutoEngineTrackerPageChange"
        @updatePageSize="onAutoEngineTrackerPageSizeChange"
      >
        <template #col-execution_type_display="{ row }">
          <span
            style="cursor:pointer;"
            :class="{
              'badge bg-gradient-info': row.execution_type === 'autoschedule',
              'badge bg-gradient-warning': row.execution_type === 'autosubstitute',
              'badge bg-gradient-secondary': row.execution_type === 'userinitiated'
            }"
            @click="openAIWGLog(row)"
            :title="row.aiwg_id ? 'View AIWG log' : 'No workgroup ID available'"
          >{{ row.execution_type_display }}<i class="material-icons ms-1" style="font-size:12px;vertical-align:middle;">description</i></span>
        </template>
      </BaseTable>
    </template>
  </ContentModal>

  <!-- AIWG Audit Log Modal -->
  <ContentModal v-model="showAIWGLogModal" :title="aiwgLogTitle" size="lg">
    <template #default>
      <div v-if="aiwgLogLoading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
        <p class="mt-2 text-muted">Loading logs...</p>
      </div>
      <div v-else-if="!aiwgLogEntries.length" class="text-center py-4 text-muted">No logs found for this workgroup.</div>
      <div v-else>
        <!-- Search + sort -->
        <div class="d-flex align-items-center gap-2 mb-3">
          <input type="text" class="form-control form-control-sm" style="max-width:260px" placeholder="Search log..." v-model="aiwgLogSearch" />
          <button class="btn btn-sm btn-outline-secondary m-0" @click="aiwgLogSortAsc = !aiwgLogSortAsc">
            <i class="material-icons text-sm align-middle">{{ aiwgLogSortAsc ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ aiwgLogSortAsc ? 'Oldest first' : 'Newest first' }}
          </button>
        </div>
        <!-- Timeline list -->
        <div class="aiwg-log-list">
          <div v-for="(entry, idx) in filteredAIWGLogEntries" :key="idx" class="aiwg-log-row">
            <div class="aiwg-log-dot" :class="'aiwg-log-dot--' + (entry.messagetype || 'info')"></div>
            <div class="aiwg-log-body">
              <span class="aiwg-log-type" :class="'aiwg-log-type--' + (entry.messagetype || 'info')">{{ entry.messagetype || 'info' }}</span>
              <div class="aiwg-log-msg">{{ entry.message }}</div>
            </div>
            <div class="aiwg-log-date" v-if="entry.messagedt">
              <div>{{ formatLogDate(entry.messagedt) }}</div>
              <div class="text-muted">{{ formatLogTime(entry.messagedt) }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ContentModal>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from 'vue-toast-notification';

import ChartHolderCard from "./components/ChartHolderCard.vue";
import ReportsBarChart from "@/components/Charts/ReportsBarChart.vue";
import ReportsLineChart from "@/components/Charts/ReportsLineChart.vue";
import MiniStatisticsCard from "./components/MiniStatisticsCard.vue";
import Modal from "@/components/Modal/Modal.vue";
import DashboardTable from "./components/DashboardTable.vue";
import CardLoader from "../components/Loader/CardLoader.vue";
import PendingRegistrations from "../modules/registrations/PendingRegistrations.vue";
import ChartSkeleton from "./components/ChartSkeleton.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import Users from "../modules/users/Users.vue";
import StatusCard from "./components/StatusCard.vue";
import AdminPermissionsModal from "@/components/AdminPermissions/AdminPermissionsModal.vue";
import SignupSummaryModal from "@/components/Modal/SignupSummaryModal.vue";
import VolunteerStatsModal from "@/components/VolunteerSummary/VolunteerStatsModal.vue";
import MessagingStatsModal from "@/components/Messaging/MessagingStatsModal.vue";
import AdminReportsCard from "@/components/AdminReports/AdminReportsCard.vue";
import LoggerService from "@/services/LoggerService";
import { getLocationId } from "@/utils/SessionDataHandler";
import { processUriPlaceholders } from "@/utils/UriHandler";
import BaseTable from '@/components/Table/Table.vue'
import AuditEventsCard from '@/components/Cards/AuditEventsCard.vue'
import VolunteerInvitationAdminCard from '@/components/Admin/VolunteerInvitationAdminCard.vue'
import PeopleGroupsPanel from "@/modules/people-groups/PeopleGroupsPanel.vue";
import { baseUrl } from "@/utils/BaseUrl.js";
import { htmlToText, truncateText } from "@/utils/HtmlToText.js";
import api from "@/config/api";
import { usePermissions } from "@/composables/usePermissions";
import { useRoute, useRouter } from "vue-router";

const store = useStore();
const toast = useToast();
const router = useRouter();
const route = useRoute();
const isPeopleGroupsView = computed(() => route.query.section === "people-groups");
const { isActivityAdmin, isLocationAdmin: permIsLocationAdmin } = usePermissions();

const isModalOpen = ref(false);
const isRefreshing = ref(false);

// Auto-refresh state
const autoRefreshEnabled = ref(false);
const autoRefreshInterval = ref(300000); // Default 5 minutes (in milliseconds)
const autoRefreshCountdown = ref(0);
let autoRefreshTimer = null;
let countdownTimer = null;
const showPendingRegistrationModal = ref(false);
const showUsersModal = ref(false);
const showMessageDetailsModal = ref(false);
const showPendingSwapsModal = ref(false);
const showPendingSwapDescriptionModal = ref(false);
const pendingSwapsLoading = ref(false);
const selectedMessage = ref(null);
const selectedPendingSwap = ref(null);
const modalHeading = ref("");
const modalContent = ref("");
const iframeUrl = ref("");
const isLoaded = ref(false);
watch([isLoaded, () => route.hash, () => route.query.section], ([loaded, hash, section]) => {
  if (!loaded || (hash !== "#admin-people-groups" && section !== "people-groups")) return;
  requestAnimationFrame(() => {
    document.getElementById("admin-people-groups")?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}, { immediate: true });
const isUsageModalActive = ref(false);
const messagingSearchTerm = ref("");
const selectedMessageTypes = ref(['email', 'sms']);
// Track cards that returned 403 (unauthorized) errors
const unauthorizedCards = ref(new Set());
// Track whether location has no activities (to highlight Setup Wizard)
const noActivitiesForLocation = ref(false);
const showAdminPermissionsModal = ref(false);
const showAiUnitsModal = ref(false);
const activitiesCardLoading = ref(true);
const activitiesCard = ref({ count: 0, total_count: 0, auto_generate_count: 0, auto_schedule_count: 0, future_instances_count: 0 });

// Admin Insight Cards state
const signupOnlyWorkgroups = ref({ total_count: 0, workgroups: [] });
const pendingSkillInterests = ref({ total_count: 0, pending_requests: [] });
const workgroupsNeedingVolunteers = ref({ total_count: 0, workgroups: [] });
const adminCardsLoading = ref({ signupOnly: true, skillInterests: true, needingVolunteers: true });
const adminCardsError = ref({ signupOnly: false, skillInterests: false, needingVolunteers: false });
const showSignupOnlyModal = ref(false);
const showSkillInterestsModal = ref(false);
const showNeedingVolunteersModal = ref(false);
const assigningSkill = ref({});
const showSignupSummaryModal = ref(false);
const selectedSignupAiwgId = ref(null);
const showVolunteerStatsModal = ref(false);
const showMessagingStatsModal = ref(false);

// Auto-Engine Tracker state
const showAutoEngineTrackerModal = ref(false);
const autoEngineTrackerData = ref({ summary: null, records: [], info: null });
const autoEngineTrackerLoading = ref(false);
const autoEngineTrackerPagination = ref(null);
const autoEngineCardData = ref({ total_executions: 0, total_cost: 0, autoschedule_count: 0, autosubstitute_count: 0 });
const autoEngineTrackerSearchWorkgroup = ref('');
const autoEngineTrackerSearchActivity = ref('');

// AIWG Audit Log state
const showAIWGLogModal = ref(false);
const aiwgLogTitle = ref('AIWG Log');
const aiwgLogEntries = ref([]);
const aiwgLogLoading = ref(false);
const aiwgLogSearch = ref('');
const aiwgLogSortAsc = ref(false);

function getFirstOfMonthDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}
function getTodayDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const autoEngineTrackerFilters = ref({
  from_date: getFirstOfMonthDate(),
  to_date: getTodayDate(),
  execution_type: ''
});

const emailChartData = ref({
  labels: ["S", "M", "T", "W", "T", "F", "S"],
  datasets: {
    label: "Email",
    data: [],
    label2: "SMS",
    data2: [],
    backgroundColor: "rgba(54, 162, 235, 0.2)",
    borderColor: "rgba(54, 162, 235, 1)",
    borderWidth: 1,
  },
});

const confirmedAssignmentChartData = ref({
  labels: [],
  datasets: { label: "Confirmed Assignments", data: [], label2: "Assigned Positions", data2: [], borderWidth: 1 },
});

const declinedAssignmentChartData = ref({
  labels: ["April", "May", "June", "July", "August", "September", "October", "November", "December"],
  datasets: { label: "Declined Assignments", data: [] },
});


// Vuex getters
const stDashboardData = computed(() => store.getters.stDashboardData);
const stLocationDetail = computed(() => store.getters.stLocationDetail);
const stMenuData = computed(() => store.getters.stMenuData);
const stSessionData = computed(() => store.getters.stSessionData);
const stPendingRegistrations = computed(() => store.getters.stPendingRegistrations);
const stAutoEngineData = computed(() => store.getters.stAutoEngineData);
const stMessagingYearlyTotals = computed(() => store.getters.stMessagingYearlyTotals);
const stMessagingDailyTotals = computed(() => store.getters.stMessagingDailyTotals);
const stMessagingSummary = computed(() => store.getters.stMessagingSummary);
const stMessagingSummaryPagination = computed(() => store.getters.stMessagingSummaryPagination);
const stUpcomingActivities = computed(() => store.getters.stUpcomingActivities);
// Sort upcoming activities by instance start date (ascending), starting from today
const sortedUpcomingActivities = computed(() => {
  const activities = stUpcomingActivities.value || [];
  if (!Array.isArray(activities) || activities.length === 0) {
    return [];
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today
  
  return [...activities]
    .filter((activity) => {
      const activityDate = new Date(activity.starttime || activity.instancedate || 0);
      return activityDate >= today; // Only include activities from today onwards
    })
    .sort((a, b) => {
      const dateA = new Date(a.starttime || a.instancedate || 0);
      const dateB = new Date(b.starttime || b.instancedate || 0);
      return dateA - dateB; // Ascending order (earliest first)
    });
});
const stAssignmentStatusCountByMonth = computed(() => store.getters.stAssignmentStatusCountByMonth);
const stPermissionData = computed(() => store.getters.stPermissionData);
const adminPermissionIds = computed(() => stPermissionData.value?.adminMenuCards || []);
const isAdminPermissionLoaded = computed(() => stPermissionData.value !== null);
const stLocationPendingSwapRequests = computed(() => store.getters.stLocationPendingSwapRequests);
const stLocationPendingSwapRequestsDetails = computed(() => store.getters.stLocationPendingSwapRequestsDetails);
const stAutoEngineStatus = computed(() => store.getters.stAutoEngineStatus);
const stMonthToDateUsage = computed(() => store.getters.stMonthToDateUsage);
const stLocationBalance = computed(() => store.getters.stLocationBalance);

// Month-to-date computed helpers for the combined cards
const mtdSms = computed(() => {
  const s = stMonthToDateUsage.value?.current_month?.sms;
  return { count: s?.count ?? 0, cost: parseFloat(s?.cost ?? 0) };
});
const mtdEmail = computed(() => {
  const e = stMonthToDateUsage.value?.current_month?.email;
  return { count: e?.count ?? 0, cost: parseFloat(e?.cost ?? 0) };
});
const mtdAiChat = computed(() => {
  const a = stMonthToDateUsage.value?.current_month?.ai_chat;
  return { count: a?.count ?? 0, cost: parseFloat(a?.cost ?? 0), input_tokens: a?.input_tokens ?? 0, output_tokens: a?.output_tokens ?? 0 };
});
const mtdAiScheduler = computed(() => {
  const a = stMonthToDateUsage.value?.current_month?.ai_scheduler;
  return { count: a?.count ?? 0, cost: parseFloat(a?.cost ?? 0), input_tokens: a?.input_tokens ?? 0, output_tokens: a?.output_tokens ?? 0 };
});
const prevAiChat = computed(() => stMonthToDateUsage.value?.previous_month?.ai_chat || null);
const prevAiScheduler = computed(() => stMonthToDateUsage.value?.previous_month?.ai_scheduler || null);

// Threshold data from getmonthtodateusage
const usageThresholds = computed(() => stMonthToDateUsage.value?.thresholds || {});

function getThresholdInfo(cost, limit) {
  if (!limit || limit <= 0) return null;
  const pct = Math.min(Math.round((cost / limit) * 100), 100);
  const color = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'success';
  return { pct, color, limit: parseFloat(limit) };
}

const smsThreshold = computed(() => getThresholdInfo(mtdSms.value.cost, usageThresholds.value.monthly_sms_cost_limit));
const aiChatThreshold = computed(() => getThresholdInfo(mtdAiChat.value.cost, usageThresholds.value.monthly_ai_chat_cost_limit));
const aiSchedulerThreshold = computed(() => getThresholdInfo(mtdAiScheduler.value.cost, usageThresholds.value.monthly_ai_schedulegeneration_cost_limit));
const overallThreshold = computed(() => getThresholdInfo(monthToDateTotalUsage.value, usageThresholds.value.monthly_spend_limit));

// Helper to check if an error is a 403 Forbidden error
function is403Error(error) {
  return error?.response?.status === 403 || error?.status === 403;
}

// Message Summary Table Configuration
const messagingColumns = [
  { key: "message_dt", label: "DATE" },
  { key: "message_data", label: "MESSAGE SUMMARY", maxWidth: "200px" },
  { key: "message_subject", label: "SUBJECT", maxWidth: "280px" },
  { key: "recipient_address", label: "RECIPIENT" },
  { key: "message_type", label: "TYPE" },
  { key: "message_direction", label: "DIRECTION" },
  { key: "delivery_status", label: "STATUS" },
  { key: "rate", label: "COST" },
];

// Message status checking
const checkingStatusFor = ref(null);

function getStatusBadge(row) {
  if (!row.status_output) return null;
  try {
    const data = JSON.parse(row.status_output);
    return {
      status: data.status || 'unknown',
      label: data.status_label || data.status || 'Unknown',
      emoji: data.status_emoji || '',
      category: data.status_category || 'Unknown',
    };
  } catch { return null; }
}

function getStatusBadgeClass(category) {
  switch (category) {
    case 'Successful': return 'badge bg-gradient-success';
    case 'Failed': return 'badge bg-gradient-danger';
    case 'Issues': return 'badge bg-gradient-warning';
    case 'Pending': return 'badge bg-gradient-info';
    default: return 'badge bg-gradient-secondary';
  }
}

function getStatusSummaryLines(row) {
  if (!row.status_output) return [];
  try {
    const data = JSON.parse(row.status_output);
    return data.summary || [];
  } catch { return []; }
}

async function checkMessageStatus(row) {
  const locationId = getLocationId();
  if (!locationId || !row.message_id) return;
  checkingStatusFor.value = row.message_id;
  try {
    const response = await api.get(`/locations/${locationId}/messages/${row.message_id}/status`);
    const result = response.data;
    // Update the row's status_output in place so the badge renders
    row.status_output = JSON.stringify(result);
    // Also update selectedMessage if it's the same message (for modal)
    if (selectedMessage.value && selectedMessage.value.message_id === row.message_id) {
      selectedMessage.value.status_output = row.status_output;
    }
  } catch (error) {
    console.error('Error checking message status:', error);
    toast.error(error.response?.data?.error || 'Failed to check message status', { position: 'top-right' });
  } finally {
    checkingStatusFor.value = null;
  }
}

const messagingSummaryRows = computed(() => {
  if (!stMessagingSummary.value || stMessagingSummary.value.length === 0) {
    return [];
  }
  return stMessagingSummary.value.map((item, index) => ({
    ...item,
    index: item.message_id || index, // Use message_id as idKey if available
  }));
});

// Pending Swap Requests Table Configuration
const pendingSwapsColumns = [
  { key: "swap_id", label: "ID" },
  { key: "swap_description", label: "DESCRIPTION" },
  { key: "requestor_activity_name", label: "REQUESTOR ACTIVITY" },
  { key: "responder_activity_name", label: "RESPONDER ACTIVITY" },
  { key: "swap_status", label: "STATUS" },
  { key: "swap_request_date", label: "REQUEST DATE" },
];

const pendingSwapsRows = computed(() => {
  if (!stLocationPendingSwapRequestsDetails.value || stLocationPendingSwapRequestsDetails.value.length === 0) {
    return [];
  }
  return stLocationPendingSwapRequestsDetails.value.map((item) => ({
    ...item,
  }));
});

function openPendingSwapDescription(row) {
  selectedPendingSwap.value = row;
  showPendingSwapDescriptionModal.value = true;
}

// Vuex actions/mutations
const fetchMessagingDailyTotals = (locationId) => store.dispatch("fetchMessagingDailyTotals", locationId);
const fetchMessagingSummary = (payload) => store.dispatch("fetchMessagingSummary", payload);
const fetchAssignmentStatusCountByMonth = (id) => store.dispatch("fetchAssignmentStatusCountByMonth", id);
const fetchLocationPendingSwapRequests = (id) => store.dispatch("fetchLocationPendingSwapRequests", id);
const fetchLocationPendingSwapRequestsDetails = (id) => store.dispatch("fetchLocationPendingSwapRequestsDetails", id);
const fetchAutoEngineStatus = (id) => store.dispatch("fetchAutoEngineStatus", id);
const fetchMonthToDateUsage = (payload) => store.dispatch("fetchMonthToDateUsage", payload);
const fetchLocationBalance = (locationId) => store.dispatch("fetchLocationBalance", locationId);
const setLocationDetails = (location) => store.commit("setLocationDetails", location);
//const setAutoEngineStatus = (status) => store.commit("setAutoEngineStatus", status);

// Admin Insight Cards API functions
async function fetchSignupOnlyWorkgroups(locationId) {
  adminCardsLoading.value.signupOnly = true;
  adminCardsError.value.signupOnly = false;
  try {
    const response = await api.get(`/locations/${locationId}/signup-only-workgroups`);
    // Handle nested response structure: response.data.data
    const responseData = response.data?.data || response.data;
    signupOnlyWorkgroups.value = {
      total_count: responseData?.total_count || 0,
      location_name: responseData?.location_name || '',
      workgroups: responseData?.workgroups || []
    };
  } catch (error) {
    console.error('Error fetching signup-only workgroups:', error);
    if (is403Error(error)) {
      adminCardsError.value.signupOnly = true;
      unauthorizedCards.value.add('signupOnlyWorkgroups');
    }
    signupOnlyWorkgroups.value = { total_count: 0, workgroups: [] };
  } finally {
    adminCardsLoading.value.signupOnly = false;
  }
}

async function fetchPendingSkillInterests(locationId) {
  adminCardsLoading.value.skillInterests = true;
  adminCardsError.value.skillInterests = false;
  try {
    const response = await api.get(`/skills/pending-interest-requests?location_id=${locationId}`);
    // Handle nested response structure: response.data.data
    const responseData = response.data?.data || response.data;
    pendingSkillInterests.value = {
      total_count: responseData?.total_count || 0,
      location_name: responseData?.location_name || '',
      pending_requests: responseData?.pending_requests || []
    };
  } catch (error) {
    console.error('Error fetching pending skill interests:', error);
    if (is403Error(error)) {
      adminCardsError.value.skillInterests = true;
      unauthorizedCards.value.add('pendingSkillInterests');
    }
    pendingSkillInterests.value = { total_count: 0, pending_requests: [] };
  } finally {
    adminCardsLoading.value.skillInterests = false;
  }
}

async function fetchWorkgroupsNeedingVolunteers(locationId) {
  adminCardsLoading.value.needingVolunteers = true;
  adminCardsError.value.needingVolunteers = false;
  try {
    const response = await api.get(`/activity-instance-workgroups/needing-volunteers?location_id=${locationId}`);
    // Handle nested response structure: response.data.data
    const responseData = response.data?.data || response.data;
    workgroupsNeedingVolunteers.value = {
      total_count: responseData?.total_count || 0,
      location_name: responseData?.location_name || '',
      workgroups: responseData?.workgroups || []
    };
  } catch (error) {
    console.error('Error fetching workgroups needing volunteers:', error);
    if (is403Error(error)) {
      adminCardsError.value.needingVolunteers = true;
      unauthorizedCards.value.add('workgroupsNeedingVolunteers');
    }
    workgroupsNeedingVolunteers.value = { total_count: 0, workgroups: [] };
  } finally {
    adminCardsLoading.value.needingVolunteers = false;
  }
}

async function fetchActivitiesCard(locationId) {
  activitiesCardLoading.value = true;
  try {
    const response = await api.get(`/locations/${locationId}/dashboard-admin-cards`);
    const data = response.data?.data || response.data;
    activitiesCard.value = data?.cards?.activities || activitiesCard.value;
  } catch (error) {
    console.error('Error fetching activity summary:', error);
  } finally {
    activitiesCardLoading.value = false;
  }
}

// Auto-Engine Tracker API function
async function fetchAutoEngineTracker(options = {}) {
  // Only location, organization, and super admins can access this endpoint
  if (!permIsLocationAdmin.value) return;

  const locationId = getLocationId();
  if (!locationId) {
    console.error('fetchAutoEngineTracker: locationId is not available');
    return;
  }

  autoEngineTrackerLoading.value = true;
  try {
    const params = new URLSearchParams();
    const fromDate = options.from_date || autoEngineTrackerFilters.value.from_date;
    const toDate = options.to_date || autoEngineTrackerFilters.value.to_date;
    const execType = options.execution_type ?? autoEngineTrackerFilters.value.execution_type;
    const page = options.page || 1;
    const pageSize = options.pageSize || 25;

    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (execType) params.append('execution_type', execType);
    params.append('page_number', page);
    params.append('page_size', pageSize);

    const response = await api.get(
      `/billing/location/${locationId}/autoengine-tracker?${params.toString()}`
    );
    // data is nested under response.data.data; info is a sibling at response.data.info
    const data = response.data?.data || response.data;
    const info = response.data?.info || null;

    autoEngineTrackerData.value = {
      summary: data?.summary || null,
      records: data?.records || [],
      location: data?.location || null,
      organization: data?.organization || null,
      date_range: data?.date_range || null,
      info: info
    };

    // Build pagination from response
    const pag = data?.pagination;
    if (pag) {
      autoEngineTrackerPagination.value = {
        currentPage: pag.current_page || page,
        pageSize: pag.page_size || pageSize,
        totalRecords: pag.total_records || 0,
        totalPages: pag.total_pages || 1
      };
    } else {
      autoEngineTrackerPagination.value = null;
    }
  } catch (error) {
    console.error('Error fetching auto-engine tracker:', error);
    autoEngineTrackerData.value = { summary: null, records: [], info: null };
    autoEngineTrackerPagination.value = null;
  } finally {
    autoEngineTrackerLoading.value = false;
  }
}

// Lightweight fetch for the card display (execution count + cost)
async function fetchAutoEngineCardSummary() {
  // Only location, organization, and super admins can access this endpoint
  if (!permIsLocationAdmin.value) return;

  const locationId = getLocationId();
  if (!locationId) return;
  try {
    const fromDate = getFirstOfMonthDate();
    const toDate = getTodayDate();
    const response = await api.get(
      `/billing/location/${locationId}/autoengine-tracker?from_date=${fromDate}&to_date=${toDate}&page_number=1&page_size=1`
    );
    const data = response.data?.data || response.data;
    const summary = data?.summary || {};
    const byType = summary.by_type || {};
    autoEngineCardData.value = {
      total_executions: summary.total_executions || 0,
      total_cost: summary.total_cost || 0,
      autoschedule_count: byType.autoschedule?.count || 0,
      autosubstitute_count: byType.autosubstitute?.count || 0,
    };
  } catch (error) {
    console.error('Error fetching auto-engine card summary:', error);
  }
}

function openSignupSummary(aiwgId) {
  selectedSignupAiwgId.value = aiwgId;
  showSignupSummaryModal.value = true;
}

async function fetchAdminInsightCards() {
  const locationId = getLocationId();
  if (!locationId) {
    console.error('fetchAdminInsightCards: locationId is not available');
    return;
  }
  
  // Fetch all 3 endpoints in parallel
  await Promise.allSettled([
    fetchSignupOnlyWorkgroups(locationId),
    fetchPendingSkillInterests(locationId),
    fetchWorkgroupsNeedingVolunteers(locationId),
    fetchActivitiesCard(locationId)
  ]);
}

// Admin Insight Cards Table Configurations
const signupOnlyColumns = [
  { key: "workgroup_name", label: "WORKGROUP" },
  { key: "activity_name", label: "ACTIVITY" },
  { key: "instance_datetime", label: "DATE/TIME" },
  { key: "persons_needed", label: "NEEDED" },
  { key: "persons_filled", label: "FILLED" },
  { key: "openings_remaining", label: "OPENINGS" },
  { key: "signup_origin", label: "ORIGIN" },
];

const signupOnlyRows = computed(() => {
  if (!signupOnlyWorkgroups.value?.workgroups?.length) return [];
  return signupOnlyWorkgroups.value.workgroups.map((item, index) => ({
    id: item.activity_instance_workgroup?.id || index,
    workgroup_name: item.activity_instance_workgroup?.name || '-',
    activity_name: item.activity?.name || '-',
    instance_datetime: item.activity_instance?.start_date_formatted && item.activity_instance?.start_time_formatted
      ? `${item.activity_instance.start_date_formatted} ${item.activity_instance.start_time_formatted}`
      : '-',
    persons_needed: item.activity_instance_workgroup?.persons_needed || 0,
    persons_filled: item.activity_instance_workgroup?.persons_filled || 0,
    openings_remaining: item.activity_instance_workgroup?.openings_remaining || 0,
    signup_origin: item.activity_instance_workgroup?.signup_origin || '-',
    skill: item.activity_instance_workgroup?.skill || '-',
    activity_description: item.activity?.description || '',
  }));
});

const skillInterestsColumns = [
  { key: "requester_name", label: "REQUESTER" },
  { key: "requester_email", label: "EMAIL" },
  { key: "skill_name", label: "SKILL" },
  { key: "request_date", label: "REQUEST DATE" },
  { key: "workgroups", label: "WORKGROUPS" },
];

const skillInterestsRows = computed(() => {
  if (!pendingSkillInterests.value?.pending_requests?.length) return [];
  return pendingSkillInterests.value.pending_requests.map((item, index) => {
    // Format workgroups list
    const workgroupsList = item.activity_workgroups?.map(wg => 
      `${wg.workgroup_name} (${wg.activity_name})`
    ).join(', ') || '-';
    
    return {
      id: `${item.requester?.id}_${item.skill?.id}_${index}`,
      requester_name: item.requester?.full_name || '-',
      requester_email: item.requester?.email || '-',
      requester_id: item.requester?.id,
      skill_name: item.skill?.name || '-',
      skill_id: item.skill?.id,
      skill_description: item.skill?.description || '',
      request_date: item.request_date && item.request_date !== '0000-00-00 00:00:00' ? item.request_date : '-',
      workgroups: workgroupsList,
      mobile_phone: item.requester?.mobile_phone || '-',
    };
  });
});

// Auto-Engine Tracker Table Configuration
const autoEngineTrackerColumns = [
  { key: "execution_type_display", label: "TYPE" },
  { key: "workgroup_name", label: "WORKGROUP" },
  { key: "activity_name", label: "ACTIVITY" },
  { key: "activity_instance_start_date", label: "INSTANCE DATE" },
  { key: "execution_start_dt", label: "STARTED" },
  { key: "execution_end_dt", label: "COMPLETED" },
  { key: "duration_minutes", label: "DURATION (MIN)" },
  { key: "cost", label: "COST" },
  { key: "ai_changes_count", label: "AI CHANGES" },
];

const autoEngineTrackerRowsRaw = computed(() => {
  if (!autoEngineTrackerData.value?.records?.length) return [];

  // The execution ID is the stable identity for a tracker row. Keep the
  // first occurrence so a legacy/API join fan-out cannot render or count the
  // same execution more than once in the table.
  const uniqueRecords = [];
  const seenExecutionIds = new Set();
  autoEngineTrackerData.value.records.forEach((item, index) => {
    const executionKey = item.id != null ? String(item.id) : `missing-id-${index}`;
    if (seenExecutionIds.has(executionKey)) return;
    seenExecutionIds.add(executionKey);
    uniqueRecords.push(item);
  });

  return uniqueRecords.map((item, index) => ({
    id: item.id || index,
    execution_type: item.execution_type || '-',
    execution_type_display: item.execution_type_display || item.execution_type || '-',
    workgroup_name: item.activity_instance_workgroup?.name || '-',
    activity_name: item.activity?.name || '-',
    aiwg_id: item.activity_instance_workgroup?.id || null,
    activity_instance_start_date: item.activity_instance?.start_dt || '-',
    execution_start_dt: item.execution_start_dt || '-',
    execution_end_dt: item.execution_end_dt || '-',
    duration_minutes: item.duration_minutes != null ? item.duration_minutes : '-',
    cost: item.cost != null ? `$${parseFloat(item.cost).toFixed(4)}` : '-',
    rate: item.rate != null ? parseFloat(item.rate).toFixed(4) : '-',
    track_id: item.track_id || '-',
    ai_changes_count: item.ai_changes_count != null ? item.ai_changes_count : '-',
  }));
});

// Apply local workgroup/activity filters
const autoEngineTrackerRows = computed(() => {
  let rows = autoEngineTrackerRowsRaw.value;
  const wgSearch = autoEngineTrackerSearchWorkgroup.value.trim().toLowerCase();
  const actSearch = autoEngineTrackerSearchActivity.value.trim().toLowerCase();
  if (wgSearch) {
    rows = rows.filter(r => r.workgroup_name.toLowerCase().includes(wgSearch));
  }
  if (actSearch) {
    rows = rows.filter(r => r.activity_name.toLowerCase().includes(actSearch));
  }
  return rows;
});

const needingVolunteersColumns = [
  { key: "activity_name", label: "ACTIVITY" },
  { key: "instance_datetime", label: "DATE/TIME" },
  { key: "workgroup_name", label: "WORKGROUP" },
  { key: "persons_needed", label: "NEEDED" },
  { key: "persons_scheduled", label: "SCHEDULED" },
  { key: "persons_confirmed", label: "CONFIRMED" },
  { key: "openings_remaining", label: "OPENINGS" },
];

const needingVolunteersRows = computed(() => {
  if (!workgroupsNeedingVolunteers.value?.workgroups?.length) return [];
  return workgroupsNeedingVolunteers.value.workgroups.map((item, index) => ({
    id: item.activity_instance_workgroup?.id || index,
    activity_name: item.activity?.name || '-',
    instance_datetime: item.activity_instance?.start_date_formatted && item.activity_instance?.start_time_formatted
      ? `${item.activity_instance.start_date_formatted} ${item.activity_instance.start_time_formatted}`
      : '-',
    workgroup_name: item.activity_instance_workgroup?.name || '-',
    persons_needed: item.activity_instance_workgroup?.persons_needed || 0,
    persons_scheduled: item.activity_instance_workgroup?.persons_scheduled || 0,
    persons_confirmed: item.activity_instance_workgroup?.persons_confirmed || 0,
    persons_declined: item.activity_instance_workgroup?.persons_declined || 0,
    openings_remaining: item.activity_instance_workgroup?.openings_remaining || 0,
    is_signup_only: item.activity_instance_workgroup?.is_signup_only || false,
    skill: item.activity_instance_workgroup?.skill || '-',
  }));
});

const hasAdminPermissions = computed(() => {
  return isAdminPermissionLoaded.value && adminPermissionIds.value.length > 0;
});

const canViewActivities = computed(() => {
  return isAdminPermissionLoaded.value && isActivityAdmin.value;
});

const visibleAdminMenuCards = computed(() => {
  if (!hasAdminPermissions.value) {
    return [];
  }

  const cards = stDashboardData.value?.adminMenuCards || [];
  return cards.filter((card) =>
    adminPermissionIds.value.includes(card?.id || card?.label)
  );
});

// The specific card IDs for the first row, in display order
const FIRST_ROW_CARD_IDS = ['pendingRegistration', 'numberOfUsers', 'admin_pending_swaps', 'billing', 'adminPermissions'];

// Build first-row cards by picking specific IDs from admin + setup cards
const adminMenuCards = computed(() => {
  if (!hasAdminPermissions.value) {
    return [];
  }

  // Combine admin menu cards and setup org cards as the card object source
  const allAdminCards = stDashboardData.value?.adminMenuCards || [];
  const setupOrgCards = stDashboardData.value?.setupOrgCards || [];
  const allCards = [...allAdminCards, ...setupOrgCards];

  // Combine permission IDs from both admin and setup org
  const allPermissionIds = [
    ...(stPermissionData.value?.adminMenuCards || []),
    ...(stPermissionData.value?.setupOrgCards || []),
  ];

  // Pick the first-row cards by ID, in order, only if user has permission
  const firstRowCards = FIRST_ROW_CARD_IDS
    .map(id => {
      if (!allPermissionIds.includes(id)) return null;
      if (unauthorizedCards.value.has(id) || unauthorizedCards.value.has(`admin_${id}`)) return null;
      return allCards.find(card => card?.id === id) || null;
    })
    .filter(Boolean);

  return processUriPlaceholders(firstRowCards, {
    locationId: stSessionData.value?.locationid || getLocationId()
  });
});

// Filter admin menu cards based on permissions
const filteredAdminMenuCards = computed(() => {
  if (!isLoaded.value) {
    return [];
  }

  return adminMenuCards.value;
});

const hasVisibleAdminCards = computed(() => visibleAdminMenuCards.value.length > 0);

const getAuthorizedAdminCard = (id) => {
  return visibleAdminMenuCards.value.find((card) => card.id === id);
};

const hasAuthorizedAdminCard = (id) => {
  // Check if card received 403 error - hide it if so
  if (unauthorizedCards.value.has(id)) {
    return false;
  }
  return Boolean(getAuthorizedAdminCard(id));
};

const filteredAdminMenuTable = computed(() => {
  const adminMenu = stMenuData.value?.adminmenu || [];
  const allowed = stPermissionData.value?.adminMenuCards;
  if (!allowed?.length) {
    return adminMenu;
  }
  console.log("adminMenu Cards", adminMenu.filter((item) => allowed.includes(item?.id)));
  return adminMenu.filter((item) => allowed.includes(item?.id));
});

// admin_permissions data from the permissions response (used by the card)
const stFullPermissionData = computed(() => store.getters.stFullPermissionData);
const ownAdminPermissions = computed(() => stFullPermissionData.value?.admin_permissions || null);

function getCardTitle(card) {
  const titleMap = {
    pendingRegistration: { text: card.label, value: stPendingRegistrations.value.pending_registration_count || 0 },
    numberOfUsers: { text: card.label, value: stLocationDetail.value.totalusers || 0 },
    admin_pending_swaps: { text: "Location Pending Swap Requests", value: stLocationPendingSwapRequests.value?.total_pending_requests || 0 },
    billing: { text: "Billing", value: billingCardValue.value },
    adminPermissions: { text: "My Admin Permissions", value: '' },
  };
  return titleMap[card.id] || { text: card.label, value: '' };
}

function getCardDetail(card) {
  const detailMap = {
    pendingRegistration: "<span class='text-success text-xs font-weight-bolder'></span> Click here to view/process Pending Registrations",
    numberOfUsers: `<span class='text-success text-xs font-weight-bolder'></span>${stLocationDetail.value.totalvolunteers} of ${stLocationDetail.value.totalusers} users are assigned to atleast 1 skill.`,
    admin_pending_swaps: pendingSwapsDetail.value,
    billing: billingCardDetail.value,
    adminPermissions: "<span class='text-xs'>View what areas you can manage</span>",
  };
  return detailMap[card.id] || '';
}

function getCardIcon(card) {
  const iconMap = {
    pendingRegistration: { name: "weekend", color: "text-white", background: "dark" },
    numberOfUsers: { name: "person", color: "text-white", background: "success" },
    admin_pending_swaps: { name: "leaderboard", color: "text-white", background: "primary" },
    billing: { name: "account_balance_wallet", color: "text-white", background: billingCardIconBackground.value },
    adminPermissions: { name: "admin_panel_settings", color: "text-white", background: "info" },
  };
  return iconMap[card.id] || { name: "info", color: "text-white", background: "secondary" };
}

function openActivitiesPage() {
  router.push({ name: "activities-events" });
}

function openModal(item) {
  if (!item) {
    return;
  }

  // Handle admin_pending_swaps card - open custom modal with table
  if (item?.id === "admin_pending_swaps") {
    openPendingSwapsModal();
    return;
  }

  // Admin Permissions card
  if (item?.id === "adminPermissions") {
    showAdminPermissionsModal.value = true;
    LoggerService.logInfo('opening modal [Admin Permissions] in Dashboard.vue');
    return;
  }

  // Month-to-date Usage modal
  if (item?.id === "usage") {
    iframeUrl.value = "";
    const periodLabel = stMonthToDateUsage.value?.current_month?.period_label;
    modalHeading.value = periodLabel ? `Usage — ${periodLabel}` : "Month-to-date Usage";
    modalContent.value = buildUsageModalContent();
    isUsageModalActive.value = true;
    isModalOpen.value = true;
    LoggerService.logInfo(`opening modal [Month-to-date Usage] in Dashboard.vue`);
    return;
  }

  // Billing modal - open cs-mf-billing in iframe
  if (item?.id === "billing") {
    iframeUrl.value = `${baseUrl}/MF/cs-mf-billing/`;
    modalHeading.value = "Billing";
    modalContent.value = ""; // Clear any previous modal content
    isUsageModalActive.value = false;
    isModalOpen.value = true;
    LoggerService.logInfo(`opening modal [Billing] in Dashboard.vue`);
    return;
  }

  // Handle original card IDs
  isUsageModalActive.value = false;
  if (item.id === "pendingRegistration") {
    showPendingRegistrationModal.value = true;
  } else if (item.id === "numberOfUsers") {
    showUsersModal.value = true;
  } else if (item.id === "schedulingUnits") {
    // Scheduling Units modal - open Auto-Engine Tracker
    showAutoEngineTrackerModal.value = true;
    fetchAutoEngineTracker();
    LoggerService.logInfo(`opening modal [Auto-Engine Tracker] in Dashboard.vue`);
  } else {
    const locationId = getLocationId();
    iframeUrl.value = item.uri
      .replace("<locationid>", locationId)
      .replace("<personid>", stSessionData.value.personId);
    modalHeading.value = item.label;
    modalContent.value = ""; // Clear any previous modal content
    isUsageModalActive.value = false; // Clear usage modal flag
    isModalOpen.value = true;
    LoggerService.logInfo(`opening modal [${item.label}] for url [${iframeUrl.value}] in Dashboard.vue`);
  }
}

function closeModal() {
  isModalOpen.value = false;
  isUsageModalActive.value = false;
  modalContent.value = ""; // Clear modal content when closing
  iframeUrl.value = ""; // Clear iframe URL when closing
}

// Handle Admin Menu item clicks (from DashboardTable)
function openProjectModal({ id, label, uri, uri_type }) {
  // Admin Permissions — open the dedicated Vue modal instead of an iframe
  if (id === 'adminPermissions') {
    showAdminPermissionsModal.value = true;
    LoggerService.logInfo('opening modal [Admin Permissions] from Admin Menu table in Dashboard.vue');
    return;
  }

  if (uri_type === "modal") {
    iframeUrl.value = uri;
    modalHeading.value = label;
    isModalOpen.value = true;
  } else {
    window.location.href = uri;
  }
  LoggerService.logInfo(`opening project modal [${label}] for url [${uri}] in Dashboard.vue`);
}

// Refresh dashboard data after modal closes
function refreshDashboardData() {
  const locationId = getLocationId();
  if (!locationId) {
    return;
  }
  
  console.log('Refreshing dashboard data after modal close...');
  
  // Clear caches for data that might have changed
  const swapCountCacheKey = `apiCache_getlocationpendingswapcount_${locationId}`;
  localStorage.removeItem(swapCountCacheKey);
  
  // Refresh admin data
  if (hasAdminPermissions.value) {
    // Refresh pending registrations, users, and other card data
    store.dispatch('fetchPendingRegistrations', locationId);
    store.dispatch('fetchLocationDetail', locationId);
    fetchLocationPendingSwapRequests(locationId);
    fetchLocationBalance(locationId).catch((error) => {
      if (is403Error(error)) {
        unauthorizedCards.value.add('admin_billing');
      }
    });
    // Refresh upcoming activities (limit to 3)
    store.dispatch('fetchUpcomingActivities', { locationid: locationId, limit: 3 });
    // Refresh admin insight cards
    fetchAdminInsightCards();
    // Refresh charts
    fetchAssignmentStatusCountByMonth(locationId).then(() => {
      prepareAssignmentsChartData();
    }).catch(() => {});
    fetchMessagingDailyTotals(locationId).then(() => {
      prepareEmailChartData();
    }).catch(() => {});
    // Refresh messaging summary
    fetchMessagingSummary({
      locationID: locationId,
      page: 1,
      pageSize: stMessagingSummaryPagination.value?.pageSize || 10,
      search: messagingSearchTerm.value,
      messageTypes: selectedMessageTypes.value
    });
  }
}

// Handle manual refresh button click
async function handleRefreshDashboard() {
  const locationId = getLocationId();
  if (!locationId) {
    toast.error('Unable to refresh: Location not found', { position: 'top-right' });
    return;
  }
  
  isRefreshing.value = true;
  console.log('Manual dashboard refresh initiated...');
  
  try {
    // Clear all relevant caches
    const swapCountCacheKey = `apiCache_getlocationpendingswapcount_${locationId}`;
    localStorage.removeItem(swapCountCacheKey);
    
    // Refresh all admin data
    if (hasAdminPermissions.value) {
      const refreshPromises = [
        store.dispatch('fetchPendingRegistrations', locationId),
        store.dispatch('fetchLocationDetail', locationId),
        fetchLocationPendingSwapRequests(locationId),
        fetchLocationBalance(locationId).catch((error) => {
          if (is403Error(error)) {
            unauthorizedCards.value.add('admin_billing');
          }
        }),
        store.dispatch('fetchUpcomingActivities', { locationid: locationId, limit: 3 }),
        fetchAssignmentStatusCountByMonth(locationId),
        fetchMessagingDailyTotals(locationId),
        fetchMessagingSummary({
          locationID: locationId,
          page: 1,
          pageSize: 10,
          search: messagingSearchTerm.value,
          messageTypes: selectedMessageTypes.value
        }),
        fetchMonthToDateUsage({
          locationid: locationId,
          personid: stSessionData.value.personId,
        }),
        // Admin insight cards
        fetchAdminInsightCards(),
        // Status Overview (Auto-Engine status)
        fetchAutoEngineStatus(locationId),
      ];
      
      await Promise.allSettled(refreshPromises);
      
      // Update charts after data refresh
      prepareEmailChartData();
      prepareAssignmentsChartData();
    }
    
    toast.success('Dashboard refreshed successfully!', {
      position: 'top-right',
      duration: 3000
    });
    
    LoggerService.logInfo('Dashboard manually refreshed by user');
  } catch (error) {
    console.error('Error refreshing dashboard:', error);
    toast.error('Failed to refresh dashboard. Please try again.', {
      position: 'top-right',
      duration: 3000
    });
  } finally {
    isRefreshing.value = false;
  }
}

// Auto-refresh functions
function toggleAutoRefresh() {
  if (autoRefreshEnabled.value) {
    // Start auto-refresh timer
    startAutoRefresh();
    toast.success(`Auto-refresh enabled (every ${autoRefreshInterval.value / 60000} minutes)`, {
      position: 'top-right',
      duration: 2000
    });
    LoggerService.logInfo(`Auto-refresh enabled with interval: ${autoRefreshInterval.value / 60000} minutes`);
  } else {
    // Stop auto-refresh timer
    stopAutoRefresh();
    toast.info('Auto-refresh disabled', {
      position: 'top-right',
      duration: 2000
    });
    LoggerService.logInfo('Auto-refresh disabled');
  }
}

function startAutoRefresh() {
  // Clear any existing timers
  stopAutoRefresh();
  
  // Set initial countdown
  autoRefreshCountdown.value = autoRefreshInterval.value / 1000;
  
  // Start countdown timer (updates every second)
  countdownTimer = setInterval(() => {
    autoRefreshCountdown.value--;
    if (autoRefreshCountdown.value <= 0) {
      autoRefreshCountdown.value = autoRefreshInterval.value / 1000;
    }
  }, 1000);
  
  // Start refresh timer
  autoRefreshTimer = setInterval(() => {
    handleRefreshDashboard();
    autoRefreshCountdown.value = autoRefreshInterval.value / 1000;
  }, autoRefreshInterval.value);
}

function stopAutoRefresh() {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
  autoRefreshCountdown.value = 0;
}

function updateAutoRefreshInterval() {
  if (autoRefreshEnabled.value) {
    // Restart with new interval
    startAutoRefresh();
    toast.info(`Refresh interval changed to ${autoRefreshInterval.value / 60000} minutes`, {
      position: 'top-right',
      duration: 2000
    });
    LoggerService.logInfo(`Auto-refresh interval changed to: ${autoRefreshInterval.value / 60000} minutes`);
  }
}

function formatCountdown(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function onMessageTypeFilterChange() {
  const locationId = getLocationId();
  if (!locationId || !hasAdminPermissions.value) return;
  fetchMessagingSummary({
    locationID: locationId,
    page: 1,
    pageSize: stMessagingSummaryPagination.value?.pageSize || 10,
    search: messagingSearchTerm.value,
    messageTypes: selectedMessageTypes.value
  });
}

function openMessageDetails(row) {
  selectedMessage.value = row;
  showMessageDetailsModal.value = true;
}

async function handleAssignSkillToUser(row) {
  const locationId = getLocationId();
  if (!locationId) {
    toast.error('Unable to assign skill: Location not found', { position: 'top-right' });
    return;
  }
  
  if (!row.requester_id || !row.skill_id) {
    toast.error('Invalid request data', { position: 'top-right' });
    return;
  }
  
  // Set loading state for this specific row
  assigningSkill.value[row.id] = true;
  
  try {
    // Make API call to assign skill to user
    await api.post('/manageuserajax', {
      cmd: 'savepersonskill',
      locationid: locationId,
      skillid: row.skill_id,
      personid: row.requester_id
    });
    
    toast.success(`Successfully assigned ${row.skill_name} to ${row.requester_name}`, {
      position: 'top-right',
      duration: 3000
    });
    
    // Refresh the pending skill interests list
    await fetchPendingSkillInterests(locationId);
    
    LoggerService.logInfo(`Assigned skill ${row.skill_id} to user ${row.requester_id}`);
  } catch (error) {
    console.error('Error assigning skill to user:', error);
    const errorMessage = error.response?.data?.message || 'Failed to assign skill. Please try again.';
    toast.error(errorMessage, {
      position: 'top-right',
      duration: 4000
    });
  } finally {
    // Clear loading state
    assigningSkill.value[row.id] = false;
  }
}

// Auto-Engine Tracker pagination & filter handlers
function onAutoEngineTrackerPageChange(page) {
  fetchAutoEngineTracker({ page, pageSize: autoEngineTrackerPagination.value?.pageSize || 25 });
}

function onAutoEngineTrackerPageSizeChange(size) {
  fetchAutoEngineTracker({ page: 1, pageSize: size });
}

function applyAutoEngineFilters() {
  fetchAutoEngineTracker({ page: 1 });
}

function resetAutoEngineFilters() {
  autoEngineTrackerFilters.value = { from_date: getFirstOfMonthDate(), to_date: getTodayDate(), execution_type: '' };
  autoEngineTrackerSearchWorkgroup.value = '';
  autoEngineTrackerSearchActivity.value = '';
  fetchAutoEngineTracker({ page: 1 });
}

// AIWG Audit Log helpers
async function openAIWGLog(row) {
  const wgId = row.aiwg_id;
  if (!wgId) {
    toast.warning('No workgroup instance ID available for this record', { position: 'top-right' });
    return;
  }
  aiwgLogTitle.value = `${row.workgroup_name || 'Workgroup'} — AIWG Log`;
  aiwgLogSearch.value = '';
  aiwgLogEntries.value = [];
  aiwgLogLoading.value = true;
  showAIWGLogModal.value = true;
  try {
    const response = await api.post('/managelocationajax', {
      cmd: 'getAIWGauditlogs',
      aworkgroupinstanceid: wgId
    });
    // Response is an array or object of log entries
    const data = response.data;
    if (Array.isArray(data)) {
      aiwgLogEntries.value = data;
    } else if (data && typeof data === 'object') {
      // Could be keyed object like { 0: {...}, 1: {...} }
      aiwgLogEntries.value = Object.values(data);
    } else {
      aiwgLogEntries.value = [];
    }
  } catch (error) {
    console.error('Error fetching AIWG audit logs:', error);
    toast.error('Failed to load AIWG logs', { position: 'top-right' });
  } finally {
    aiwgLogLoading.value = false;
  }
}

const filteredAIWGLogEntries = computed(() => {
  let entries = [...aiwgLogEntries.value];
  // Search filter
  const q = aiwgLogSearch.value.trim().toLowerCase();
  if (q) {
    entries = entries.filter(e =>
      (e.message || '').toLowerCase().includes(q) ||
      (e.messagetype || '').toLowerCase().includes(q) ||
      (e.messagedt || '').toLowerCase().includes(q)
    );
  }
  // Sort
  entries.sort((a, b) => {
    const da = a.messagedt || '';
    const db = b.messagedt || '';
    return aiwgLogSortAsc.value ? da.localeCompare(db) : db.localeCompare(da);
  });
  return entries;
});

function formatLogDate(dt) {
  if (!dt) return '';
  try { return new Date(dt.substring(0, 10)).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return dt.substring(0, 10); }
}

function formatLogTime(dt) {
  if (!dt || dt.length < 19) return '';
  return dt.substring(11, 19);
}

async function openPendingSwapsModal() {
  const locationId = getLocationId();
  if (!locationId) {
    console.error('openPendingSwapsModal: locationId is not available');
    return;
  }
  
  pendingSwapsLoading.value = true;
  showPendingSwapsModal.value = true;
  
  try {
    await fetchLocationPendingSwapRequestsDetails(locationId);
    LoggerService.logInfo(`opened Pending Swap Requests modal in Dashboard.vue`);
  } catch (error) {
    console.error('Error fetching pending swap requests details:', error);
  } finally {
    pendingSwapsLoading.value = false;
  }
}

function getDayIndex(day) {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days.indexOf(day);
}

function prepareEmailChartData() {
  const emailData = [0, 0, 0, 0, 0, 0, 0];
  const smsData = [0, 0, 0, 0, 0, 0, 0];

  // Ensure stMessagingDailyTotals.value is an array before iterating
  const dailyTotals = stMessagingDailyTotals.value;

  // Handle all possible data types: array, object, string, null, undefined
  if (Array.isArray(dailyTotals) && dailyTotals.length > 0) {
    dailyTotals.forEach((entry) => {
      if (entry && typeof entry === 'object') {
        const dayIndex = getDayIndex(entry.day_of_week);
        if (dayIndex !== -1) {
          if (entry.message_type === "email") {
            emailData[dayIndex] = parseInt(entry.total_messages, 10) || 0;
          } else if (entry.message_type === "sms") {
            smsData[dayIndex] = parseInt(entry.total_messages, 10) || 0;
          }
        }
      }
    });
  }
  emailChartData.value.datasets.data = emailData;
  emailChartData.value.datasets.data2 = smsData;
}

function prepareAssignmentsChartData() {
  // Check if data exists and is an array
  if (!stAssignmentStatusCountByMonth.value || !Array.isArray(stAssignmentStatusCountByMonth.value)) {
    console.warn('prepareAssignmentsChartData: No valid data available', stAssignmentStatusCountByMonth.value);
    return;
  }

  console.log('Preparing chart data from:', stAssignmentStatusCountByMonth.value);

  // Use labels directly from API data (already sorted chronologically, may include year suffix e.g. "Jun 25")
  const labels = [];
  const confirmedData = [];
  const assignedData = [];
  const declinedData = [];

  stAssignmentStatusCountByMonth.value.forEach((entry) => {
    labels.push(entry.month);
    confirmedData.push(parseInt(entry.confirmed_count || 0, 10));
    assignedData.push(parseInt(entry.total_assigned_count || 0, 10));
    declinedData.push(parseInt(entry.declined_count || 0, 10));
  });

  // Update chart data
  confirmedAssignmentChartData.value.labels = labels;
  declinedAssignmentChartData.value.labels = labels;
  confirmedAssignmentChartData.value.datasets.data = confirmedData;
  confirmedAssignmentChartData.value.datasets.data2 = assignedData;
  declinedAssignmentChartData.value.datasets.data = declinedData;
  
  console.log('Chart data prepared:', {
    labels: labels,
    confirmed: confirmedData,
    assigned: assignedData,
    declined: declinedData
  });
}

function getCountValues() {
  const locationId = getLocationId();
  if (!locationId) {
    console.error('getCountValues: locationId is not available');
    return;
  }
  
  // Only fetch admin-specific data if user has admin permissions
  if (hasAdminPermissions.value) {
    console.log('getCountValues: Fetching admin-specific APIs for locationId:', locationId);
    // Clear cache for pending swap requests to ensure fresh data on refresh
    const swapCountCacheKey = `apiCache_getlocationpendingswapcount_${locationId}`;
    localStorage.removeItem(swapCountCacheKey);
    
    console.log('getCountValues: Calling fetchAssignmentStatusCountByMonth');
    fetchAssignmentStatusCountByMonth(locationId).then(() => {
      console.log('getCountValues: fetchAssignmentStatusCountByMonth completed');
      // Chart will be updated by the watcher, but call it here too for immediate update
      prepareAssignmentsChartData();
    }).catch((error) => {
      console.error('getCountValues: Error fetching assignment status count:', error);
    });
    fetchMessagingDailyTotals(locationId).then(() => {
      prepareEmailChartData();
      isLoaded.value = true;
    });
    fetchAutoEngineStatus(locationId).then(() => {
      console.log("called fetchAutoEngineStatus");
    });
    fetchLocationPendingSwapRequests(locationId);
    // Fetch messaging summary with pagination
    fetchMessagingSummary({
      locationID: locationId,
      page: 1,
      pageSize: 10,
      search: messagingSearchTerm.value,
      messageTypes: selectedMessageTypes.value
    });
    // Fetch billing data
      fetchLocationBalance(locationId).then((locationData) => {
        console.log('Location balance fetched:', locationData);
      }).catch((error) => {
        console.error('Error fetching location balance:', error);
        // Hide billing card if 403 error
        if (is403Error(error)) {
          unauthorizedCards.value.add('admin_billing');
        }
      });
    // Fetch admin insight cards (signup-only workgroups, pending skill interests, workgroups needing volunteers)
    fetchAdminInsightCards();
    // Fetch auto-engine card summary for the "Auto-Engine Executions" card
    fetchAutoEngineCardSummary();
  } else {
    console.log('getCountValues: Admin permissions not available yet, will be called when permissions load');
    // For volunteers, just set isLoaded to true
    isLoaded.value = true;
  }
}

// Pagination handlers for messaging table
function onMessagingPageChange(page) {
  if (!hasAdminPermissions.value) {
    return;
  }
  if (!page || page < 1) {
    console.warn('Invalid page number:', page);
    return;
  }
  const locationId = getLocationId();
  if (!locationId) {
    console.error('Location ID not available for pagination');
    return;
  }
  console.log('Changing to page:', page);
  fetchMessagingSummary({
    locationID: locationId,
    page: page,
    pageSize: stMessagingSummaryPagination.value?.pageSize || 10,
    search: messagingSearchTerm.value || "",
    messageTypes: selectedMessageTypes.value
  });
}

function onMessagingPageSizeChange(size) {
  if (!hasAdminPermissions.value) {
    return;
  }
  if (!size || size < 1) {
    console.warn('Invalid page size:', size);
    return;
  }
  const locationId = getLocationId();
  if (!locationId) {
    console.error('Location ID not available for page size change');
    return;
  }
  console.log('Changing page size to:', size);
  fetchMessagingSummary({
    locationID: locationId,
    page: 1,
    pageSize: size,
    search: messagingSearchTerm.value || "",
    messageTypes: selectedMessageTypes.value
  });
}

function onMessagingSearchChanged(term) {
  if (!hasAdminPermissions.value) {
    return;
  }
  messagingSearchTerm.value = term || "";
  const locationId = getLocationId();
  if (!locationId) {
    console.error('Location ID not available for search');
    return;
  }
  console.log('Searching with term:', term);
  fetchMessagingSummary({
    locationID: locationId,
    page: 1,
    pageSize: stMessagingSummaryPagination.value?.pageSize || 10,
    search: term || "",
    messageTypes: selectedMessageTypes.value
  });
}

function resetLocationDetails() {
  // Reset state to a default or cleared value
  // This can be used if needed
}

async function fetchUsageData() {
  const locationId = getLocationId();

  if (!locationId) {
    console.error("fetchUsageData: locationId is not available");
    return;
  }

  try {
    // Single call now returns both current_month and previous_month
    await fetchMonthToDateUsage({
      locationid: locationId,
      personid: stSessionData.value.personId,
    });
  } catch (error) {
    console.error("Error fetching month-to-date usage data:", error);
  }
}

function buildUsageSectionHtml(monthData, heading) {
  if (!monthData) return '';
  const schedulingMinutes = monthData.autoengine?.minutes ?? 0;
  const schedulingCost = parseFloat(monthData.autoengine?.cost ?? 0);
  const smsCount = monthData.sms?.count ?? 0;
  const smsCost = parseFloat(monthData.sms?.cost ?? 0);
  const emailCount = monthData.email?.count ?? 0;
  const emailCost = parseFloat(monthData.email?.cost ?? 0);
  const platformCost = parseFloat(monthData.platform_fee ?? 0);
  const totalCost = parseFloat(monthData.total_cost ?? (schedulingCost + smsCost + emailCost + platformCost));

  const aiChatCost = parseFloat(monthData.ai_chat?.cost ?? 0);
  const aiChatIn = monthData.ai_chat?.input_tokens ?? 0;
  const aiChatOut = monthData.ai_chat?.output_tokens ?? 0;
  const aiSchedCost = parseFloat(monthData.ai_scheduler?.cost ?? 0);
  const aiSchedIn = monthData.ai_scheduler?.input_tokens ?? 0;
  const aiSchedOut = monthData.ai_scheduler?.output_tokens ?? 0;

  const rows = [
    { label: "Scheduling Units", detail: `${schedulingMinutes} minutes`, costLabel: `$${schedulingCost.toFixed(2)}` },
    { label: "SMS Messaging", detail: `${smsCount} messages`, costLabel: `$${smsCost.toFixed(2)}` },
    { label: "Email Messaging", detail: `${emailCount} messages`, costLabel: `$${emailCost.toFixed(2)}` },
    { label: "AI Chat", detail: `In: ${aiChatIn.toLocaleString()} · Out: ${aiChatOut.toLocaleString()}`, costLabel: `$${aiChatCost.toFixed(2)}` },
    { label: "AI Schedule Gen", detail: `In: ${aiSchedIn.toLocaleString()} · Out: ${aiSchedOut.toLocaleString()}`, costLabel: `$${aiSchedCost.toFixed(2)}` },
    { label: "Platform Fee", detail: null, costLabel: `$${platformCost.toFixed(2)}` },
    { label: "Total Cost", detail: null, costLabel: `$${totalCost.toFixed(2)}`, isTotal: true },
  ];

  return `
    <div style="margin-bottom:1.5rem;">
      <h6 style="font-weight:700;margin-bottom:0.75rem;">${heading}</h6>
      <div style="display:flex;flex-direction:column;gap:12px;">
        ${rows.map((row, index) => {
          const isLast = index === rows.length - 1;
          const borderStyle = isLast ? "border-bottom:0" : "border-bottom:1px solid #e9ecef;";
          const fontWeight = row.isTotal ? "font-weight:700;" : "font-weight:600;";
          return `
            <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;padding-bottom:0.75rem;${borderStyle}">
              <div>
                <div style="${fontWeight}">${row.label}</div>
                ${row.detail ? `<div style="color:#6c757d;font-size:0.9rem;">${row.detail}</div>` : ""}
              </div>
              <div style="${fontWeight}">${row.costLabel}</div>
            </div>`;
        }).join("")}
      </div>
    </div>`;
}

function buildUsageModalContent() {
  const cm = stMonthToDateUsage.value?.current_month;
  const pm = stMonthToDateUsage.value?.previous_month;

  // Current month section
  const currentHeading = cm?.period_label || 'Current Month';
  let html = buildUsageSectionHtml(cm, currentHeading);

  // Previous month section (collapsible)
  if (pm) {
    const prevHeading = pm.period_label || 'Previous Month';
    html += `<details style="margin-top:0.5rem;"><summary style="cursor:pointer;font-weight:600;margin-bottom:0.5rem;">Previous Month (${prevHeading})</summary>`;
    html += buildUsageSectionHtml(pm, prevHeading);
    html += `</details>`;
  }

  return html;
}

function generateStatusTableRows() {
  // Ensure stAutoEngineStatus exists and has the required properties
  console.log("in generateStatusTableRows");
  console.log(stAutoEngineStatus.value);

  if (!stAutoEngineStatus.value) {
    return [];
  }

  let autoenginemessage =
    stAutoEngineStatus.value.data["Latest End message"] + " at approximately: " + stAutoEngineStatus.value.data["Next start date/time"];
  if (stAutoEngineStatus.value.data["is_auto_engine_running"]) {
    autoenginemessage = stAutoEngineStatus.value.data["auto_engine_message"];
    if (stAutoEngineStatus.value.data["auto_engine_instance_activity"] != "") {
      autoenginemessage +=
        " for Activity: " +
        stAutoEngineStatus.value.data["auto_engine_instance_activity"] +
        ", Instance/Date: " +
        stAutoEngineStatus.value.data["auto_engine_instance_label"] +
        ", Instance Workgroup: " +
        stAutoEngineStatus.value.data["auto_engine_instance_workgroup_name"];
    }
  }

  // Build Billing Credit row from location_billing_status
  const billing = stAutoEngineStatus.value.data?.location_billing_status;
  const billingRow = buildBillingCreditRow(billing);

  return [
    // Row 1: Organization Status
    {
      item: "Organization status",
      status: stAutoEngineStatus.value.data["Organization status"] || "N/A",
      description: stAutoEngineStatus.value.data["Organization status message"] || "N/A",
    },
    // Row 2: Location Status
    {
      item: "Location status",
      status: stAutoEngineStatus.value.data["Location status"] || "N/A",
      description: stAutoEngineStatus.value.data["Location status message"] || "N/A",
    },
    // Row 3: Billing Credit (immediately after Location status)
    billingRow,
    // Row 4: Auto-Engine Status
    {
      item: "Auto-Engine status",
      status: stAutoEngineStatus.value.data["is_auto_engine_running"] ? "Executing" : "Idle",
      description: autoenginemessage,
    },
    // Row 5: Notification Status
    {
      item: "Notification status",
      status: stAutoEngineStatus.value.data["Location Notification status"] || "N/A",
      description: stAutoEngineStatus.value.data["Location Notification status message"] || "N/A",
    },
  ];
}

function buildBillingCreditRow(billing) {
  if (!billing) {
    return { item: "Billing Credit", status: "N/A", description: "Billing status unavailable" };
  }

  const statusMap = {
    ok:                 { badge: "Funded",             description: "" },
    warning:            { badge: "Low Balance",        description: billing.message || "" },
    insufficient_funds: { badge: "Insufficient Funds", description: billing.message || "" },
    unknown:            { badge: "Unknown",            description: billing.message || "N/A" },
  };

  const mapped = statusMap[billing.status] || statusMap.unknown;
  return { item: "Billing Credit", status: mapped.badge, description: mapped.description };
}

// Banner text shown above the Status Overview table when funds are insufficient
const billingStatusBanner = computed(() => {
  const billing = stAutoEngineStatus.value?.data?.location_billing_status;
  if (billing?.status === 'insufficient_funds') {
    return 'The auto-engine is currently suspended for this location due to insufficient billing credit. Please add credit to resume scheduling.';
  }
  return '';
});

// Computed property for total month-to-date usage
const monthToDateTotalUsage = computed(() => {
  const cm = stMonthToDateUsage.value?.current_month;
  if (cm?.total_cost != null) return parseFloat(cm.total_cost);
  // Fallback: sum from individual sources
  const schedulingCost = parseFloat(stAutoEngineData.value?.total_cost || 0);
  const smsCost = parseFloat(stMessagingYearlyTotals.value?.sms?.total_cost || 0);
  const emailCost = parseFloat(stMessagingYearlyTotals.value?.email?.total_cost || 0);
  const platformCost = parseFloat(cm?.platform_fee || 0);
  return schedulingCost + smsCost + emailCost + platformCost;
});

const usageDetail = computed(() => {
  const cm = stMonthToDateUsage.value?.current_month;
  const pm = stMonthToDateUsage.value?.previous_month;
  const currentmonthcost = cm?.total_cost ?? 0;
  const lastmonthcost = pm?.total_cost ?? 0;
  if (currentmonthcost > lastmonthcost) {
    return `<span class='text-warning text-xs font-weight-bolder'>Usage Increased: </span>The usage cost for this month has exceeded last month: $${parseFloat(lastmonthcost).toFixed(2)} .`;
  } else {
    return `<span class='text-success text-xs font-weight-bolder'>Last Month's Usage Cost:</span> <b>$${parseFloat(lastmonthcost).toFixed(2)}</b>`;
  }
});

const pendingSwapsDetail = computed(() => {
  const count = stLocationPendingSwapRequests.value?.total_pending_requests || 0;

  if (count > 0) {
    return `<span class='text-warning text-xs font-weight-bolder'>Pending Swaps. </span>There are swap requests that are still pending .`;
  } else {
    return `<span class='text-success text-xs font-weight-bolder'>No Swap Requests</span> There are no pending swap requests for this location.`;
  }
});

// Billing card computed properties
const billingThreshold = computed(() => {
  return parseFloat(stLocationBalance.value?.billing_auto_replenish_thresh || 0);
});

const billingBalance = computed(() => {
  return parseFloat(stLocationBalance.value?.current_balance || 0);
});

const billingCreditState = computed(() => {
  if (!stLocationBalance.value) {
    return 'loading';
  }
  if (billingBalance.value <= 0) {
    return 'critical';
  }
  if (billingThreshold.value > 0 && billingBalance.value < billingThreshold.value) {
    return 'low';
  }
  return 'ok';
});

const billingCardClass = computed(() => {
  if (billingCreditState.value === 'critical') {
    return 'billing-card-critical';
  }
  if (billingCreditState.value === 'low') {
    return 'billing-card-low';
  }
  return '';
});

const billingCardValue = computed(() => {
  // Display current_balance exactly as returned from API
  const currentBalance = stLocationBalance.value?.current_balance;
  if (currentBalance === undefined || currentBalance === null) {
    return '$0.00';
  }
  return `$${currentBalance}`;
});

const billingCardIconBackground = computed(() => {
  if (billingCreditState.value === 'critical') {
    return 'danger'; // red
  } else if (billingCreditState.value === 'low') {
    return 'warning'; // orange
  }
  return 'success'; // green
});

const billingCardDetail = computed(() => {
  const locationData = stLocationBalance.value;
  
  if (!locationData) {
    return "<span class='text-muted text-xs'>Loading billing information...</span>";
  }
  
  const isSelfFunded = locationData.billing_self_funded;
  const fundingMethod = locationData.funding_method || (isSelfFunded ? 'Self-funded' : 'Organization Funded');
  const fundingSummary = locationData.organization_funding_summary || '';
  
  let detail = `<span class='text-xs font-weight-bolder'>${fundingMethod}</span>`;
  
  // Display organization_funding_summary from API if available
  if (fundingSummary) {
    detail += `<br/><span class='text-xs'>${fundingSummary}</span>`;
  }

  if (billingCreditState.value === 'critical') {
    detail += `<br/><span class='billing-card-alert'><i class='material-icons'>warning</i> Credit exhausted — add funds to resume service.</span>`;
  } else if (billingCreditState.value === 'low') {
    detail += `<br/><span class='billing-card-alert'><i class='material-icons'>warning</i> Below the $${billingThreshold.value.toFixed(2)} replenishment threshold.</span>`;
  }
  
  return detail;
});

watch(stLocationDetail, (newLocation) => {
  setLocationDetails(newLocation);
  // Optional: Reset state or perform additional actions if location is null/undefined
  if (!newLocation) {
    resetLocationDetails();
  }
});

watch(stMonthToDateUsage, (newData) => {
  if (newData) {
    console.log('Month-to-date usage data updated:', newData);
    // Update modal content if modal is active
    if (isUsageModalActive.value) {
      modalContent.value = buildUsageModalContent();
    }
  }
}, { immediate: true, deep: true });

// Add a watcher for assignment status data to update charts reactively
watch(stAssignmentStatusCountByMonth, (newData) => {
  if (newData && Array.isArray(newData) && newData.length > 0) {
    console.log('Assignment status data updated, preparing chart data:', newData);
    prepareAssignmentsChartData();
  }
}, { immediate: true, deep: true });

// Watch for when admin permissions are loaded, then fetch the APIs.
// This covers the race condition where onMounted fires before permissions are available.
watch([hasAdminPermissions, isAdminPermissionLoaded], ([hasAdmin, permLoaded]) => {
  if (permLoaded && hasAdmin && !isPeopleGroupsView.value) {
    console.log('Admin permissions now available — calling full data fetch');
    getCountValues();
    fetchUsageData();
  }
}, { immediate: true });

// Refresh data when modals close (instead of full page reload)
watch(showPendingRegistrationModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    // Modal closed - refresh relevant data
    refreshDashboardData();
  }
});

watch(showUsersModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    // Modal closed - refresh relevant data
    refreshDashboardData();
  }
});

watch(isModalOpen, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    // Modal closed - refresh relevant data
    refreshDashboardData();
  }
});

watch(showPendingSwapsModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshDashboardData();
  }
});

watch(showSignupOnlyModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshDashboardData();
  }
});

watch(showSkillInterestsModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshDashboardData();
  }
});

watch(showNeedingVolunteersModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshDashboardData();
  }
});

watch(showAutoEngineTrackerModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshDashboardData();
  }
});

watch(showAiUnitsModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshDashboardData();
  }
});

// Watch for auto engine status changes
// watch(stAutoEngineStatus, (newStatus) => {
//   setAutoEngineStatus(newStatus);
// });

async function checkLocationActivities() {
  try {
    const locId = getLocationId();
    if (!locId) return;
    const response = await api.get(`/activities/location/${locId}?page_size=1&page_number=1`);
    const activities = response.data?.data || response.data || [];
    const totalRecords = response.data?.pagination?.total_records ?? activities.length;
    noActivitiesForLocation.value = totalRecords === 0;
  } catch (e) {
    console.error('Error checking location activities:', e);
  }
}

onMounted(() => {
  if (isPeopleGroupsView.value) {
    return;
  }

  // Clear cache for APIs that should refresh on page load
  const locationId = getLocationId();
  if (locationId) {
    // Clear cache for getlocationpendingswapcount
    const swapCountCacheKey = `apiCache_getlocationpendingswapcount_${locationId}`;
    localStorage.removeItem(swapCountCacheKey);
    console.log('Cleared cache for getlocationpendingswapcount on page refresh');
  }
  
  getCountValues();
  // Only fetch usage data if user has admin permissions
  // Note: This might not run if permissions aren't loaded yet, so the watcher above will handle it
  if (hasAdminPermissions.value) {
    fetchUsageData();
  }
  // Check if location has activities (to highlight Setup Wizard in admin menu)
  checkLocationActivities();
});

onUnmounted(() => {
  // Clean up auto-refresh timers when component is destroyed
  stopAutoRefresh();
});
</script>

<style scoped>
/* 👇 your skeleton loader styles remain unchanged */
.h-full {
  height: 100%;
}

/* Make cards equal height in rows */
.row>[class*="col-"] {
  display: flex;
  flex-direction: column;
}

.row>[class*="col-"]>.card,
.row>[class*="col-"]>mini-statistics-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.row>[class*="col-"]>.card>.card-header,
.row>[class*="col-"]>.card>.card-footer {
  flex-shrink: 0;
}

.row>[class*="col-"]>.card>.card-body {
  flex: 1;
}

.skeleton-animate {
  animation: skeleton-loading 1.5s infinite;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.skeleton-icon {
  width: 24px;
  height: 24px;
  margin: auto;
}

.skeleton-label {
  height: 12px;
  width: 80px;
  margin-left: auto;
}

.skeleton-value {
  height: 24px;
  width: 50px;
  margin-left: auto;
}

.skeleton-detail {
  height: 12px;
  width: 100%;
}

.skeleton-chart-area {
  height: 200px;
  margin: 20px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.3) 25%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0.3) 75%);
  background-size: 200% 100%;
}

.skeleton-title {
  height: 18px;
  width: 150px;
}

.skeleton-subtitle {
  height: 14px;
  width: 100%;
}

.skeleton-table-title {
  height: 24px;
  width: 200px;
}

.skeleton-header {
  height: 14px;
  width: 80%;
  margin: 8px 0;
}

.skeleton-cell {
  height: 16px;
  width: 90%;
  margin: 12px 0;
}

.skeleton-text {
  height: 14px;
  border-radius: 4px;
  margin: 8px 0;
}

.loader {
  width: 48px;
  height: 48px;
  border: 5px solid green;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Refresh Card Styles */
.refresh-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.refresh-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.refresh-btn {
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.refresh-btn:active:not(:disabled) {
  transform: translateY(0);
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Auto-refresh controls */
.auto-refresh-controls {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 12px;
  margin-top: 12px;
}

.auto-refresh-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.auto-refresh-toggle .form-check-input {
  width: 40px;
  height: 20px;
  cursor: pointer;
}

.auto-refresh-toggle label {
  font-size: 0.875rem;
  color: #495057;
  font-weight: 500;
  cursor: pointer;
  margin: 0;
}

.auto-refresh-options {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.auto-refresh-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.auto-refresh-select:hover {
  border-color: #6c757d;
}

.auto-refresh-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.auto-refresh-countdown {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
}

.countdown-timer {
  font-family: monospace;
  color: #3b82f6;
  font-weight: 600;
}

/* Message Details Modal Styles */
.message-details {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.log-details-list {
  margin: 0;
  padding: 0;
}

.log-item {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.log-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.log-item dt {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.log-item dd {
  margin: 0;
  color: #212529;
  font-size: 0.95rem;
  word-wrap: break-word;
}

.log-message {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: visible;
  max-height: none;
}

/* Admin Insight Cards Styles */
.admin-insight-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.admin-insight-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.admin-insight-card .card-body {
  padding: 1.25rem;
}

.admin-insight-card .icon-shape {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.count-badge {
  display: inline-block;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 12px;
  min-width: 80px;
  text-align: center;
  line-height: 1;
}

.admin-insight-card .btn-outline-info:disabled,
.admin-insight-card .btn-outline-warning:disabled,
.admin-insight-card .btn-outline-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.admin-insight-card .btn {
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Dashboard heading */
.dashboard-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.dh-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
}
.dh-icon-wrap i { font-size: 28px; }
.dh-icon-admin { background: linear-gradient(135deg, #42a5f5, #1565c0); }
.dh-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #344767;
}
.dh-subtitle {
  margin: 0;
  font-size: 0.78rem;
  color: #7b809a;
  margin-top: 2px;
}
/* AIWG Log Timeline */
.aiwg-log-list { position: relative; padding-left: 26px; }
.aiwg-log-list::before { content: ''; position: absolute; left: 10px; top: 0; bottom: 0; width: 2px; background: #e0e0e0; }
.aiwg-log-row { display: grid; grid-template-columns: auto 1fr auto; column-gap: 14px; margin-bottom: 14px; }
.aiwg-log-dot { width: 16px; height: 16px; border-radius: 50%; background: #9e9e9e; margin-top: 2px; }
.aiwg-log-dot--info { background: #ff6200; }
.aiwg-log-dot--update { background: #00C853; }
.aiwg-log-dot--delete { background: #6D4C41; }
.aiwg-log-dot--error { background: #F44336; }
.aiwg-log-dot--warning { background: #FFAB00; }
.aiwg-log-body { font-size: 0.82rem; }
.aiwg-log-type { display: inline-block; padding: 1px 8px; font-size: 10px; font-weight: 600; text-transform: uppercase; border-radius: 10px; color: #fff; margin-bottom: 2px; }
.aiwg-log-type--info { background: #ff6200; }
.aiwg-log-type--update { background: #00C853; }
.aiwg-log-type--delete { background: #6D4C41; }
.aiwg-log-type--error { background: #F44336; }
.aiwg-log-type--warning { background: #FFAB00; }
.aiwg-log-msg { color: #344767; }
.aiwg-log-date { text-align: right; white-space: nowrap; font-size: 11px; }

:deep(.billing-card-low) {
  border: 2px solid #f59e0b;
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.28);
  background: linear-gradient(145deg, #fffdf7 0%, #fff7e6 100%);
}

:deep(.billing-card-critical) {
  border: 2px solid #dc2626;
  box-shadow: 0 8px 24px rgba(220, 38, 38, 0.3);
  background: linear-gradient(145deg, #fffafa 0%, #fff0f0 100%);
  animation: billing-card-pulse 2.2s ease-in-out infinite;
}

:deep(.billing-card-low .card-header p),
:deep(.billing-card-critical .card-header p) {
  font-weight: 800;
}

:deep(.billing-card-alert) {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 4px;
  color: #b45309;
  font-weight: 800;
}

:deep(.billing-card-critical .billing-card-alert) {
  color: #b91c1c;
}

:deep(.billing-card-alert i) {
  font-size: 14px;
  vertical-align: middle;
}

@keyframes billing-card-pulse {
  0%, 100% { box-shadow: 0 8px 24px rgba(220, 38, 38, 0.24); }
  50% { box-shadow: 0 8px 30px rgba(220, 38, 38, 0.45); }
}
</style>
